/* eslint-disable react/no-array-index-key */
import axios from 'axios';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { parseFullName } from 'parse-full-name';
import {
  message,
  Input,
  Radio,
  Form,
  Button,
  Select,
  Checkbox,
  Modal,
  AutoComplete,
  Tooltip,
  Divider,
  Spin,
} from 'antd';
import DatePicker from '../DatePicker';
import { InfoCircleOutlined } from '@ant-design/icons';
import { validPhoneNumber, validZipCode, validSendEmail } from '../../global/validation';
import { verifyFeatureAccess } from '../../global/featureFlags';
import ModalWrapper from './ModalWrapper';
import { extractReplyTiffWarnings, showReplyTiffWarningModal } from '../chartActions/replyTiffWarning';
import BoundChartChip from './BoundChartChip';
import PatientMatchSearch from './PatientMatchSearch';
import { buildMissingMessage } from './chartModalHelpers';
import ReferringProviderAutocomplete from '../autocomplete/ReferringProviderAutocomplete';
import { taskCreatedByStamp, buildTaskTemplateNotes } from '../../utils/taskTemplateNotes';
import dedupeDocumentCategories from '../../utils/dedupeDocumentCategories';
import './chartModal.scss';

import {
  policyTypes,
  priorityOptions,
  taskOptions,
  taskDetailsOptions,
  authorizationTypeOptions,
} from './constants';

const usStates = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  // Canadian provinces/territories
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Québec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon Territory' },
  // Other territories
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'MP', label: 'Northern Mariana Islands' },
  { value: 'GU', label: 'Guam' },
  { value: 'AS', label: 'American Samoa' },
  { value: 'VI', label: 'Virgin Islands' },
  { value: 'MX', label: 'Mexico' },
  // Armed Forces
  { value: 'AE', label: 'Armed Forces (Africa, Europe, Canada, Middle East)' },
  { value: 'AA', label: 'Armed Forces Americas' },
  { value: 'AP', label: 'Armed Forces Pacific' },
];

const { confirm } = Modal;

// Section heading + provenance pill for the patient-first EMA/Kareo layout.
const SECTION_HEADING_STYLE = {
  display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: '14px', margin: '12px 0 8px', color: '#1f1a2b',
};
const SECTION_PILL_STYLE = {
  marginLeft: 8, fontSize: '10px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5e3999', background: '#efe9f7', borderRadius: '999px', padding: '2px 8px',
};

export class CreateNewChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fileName: props.modalType.data.fileName,
      sendToken: props.modalType.data.record,
      documentCategory: props.modalType.data.documentCategory || '',
      documentCategories: [],
      apiPatientsList: [],
      genderOptions: [
        { label: 'Male', value: 'male' },
        { label: 'Female', value: 'female' },
        { label: 'Other', value: 'other' },
      ],
      patientName: this.props.modalType.data.name,
      patientDOB: this.props.modalType.data.dob,
      patientGender: this.props.modalType.data.gender?.toLowerCase() || '',
      patientPhoneNumber: this.props.modalType.data.phoneNumber || '',
      receiverType: this.props.modalType.data.receiverType,
      patientZipCode: this.props.modalType.data.zipCode || '',
      patientCity: this.props.modalType.data.city || '',
      practitionerName: this.props.modalType.data.practitionerName || '',
      patientAddress: this.props.modalType.data.address || '',
      patientState: this.props.modalType.data.state || '',
      patientEmail: this.props.modalType.data.patient_email || '',
      referringProvider: this.props.modalType.data.referring_provider || '',
      referringProviderId: null,
      providerOffice: this.props.modalType.data.provider_office || '',
      primaryPlan: this.props.modalType.data.payerName || this.props.modalType.data.primary_plan || '',
      primaryPlanName: this.props.modalType.data.primaryPlanName || '',
      primaryMemberId: this.props.modalType.data.primaryMemberId || '',
      caseName: '',
      apiCredentials: this.props.modalType.data.apiCredentials,
      patientFirstName: this.props.modalType.data.patient_first_name || '',
      patientMiddleName: this.props.modalType.data.patient_middle_name || '',
      patientLastName: this.props.modalType.data.patient_last_name || '',
      secondaryPhone: '',
      practiceName: '',
      searchedPrimaryPayerNames: [],
      populateInsurance: localStorage.populateInsurance === 'true',
      secondaryInsurance: false,
      primaryPolicyType: '',
      policyTypeOptions: [],
      searchedPrimaryPolicyTypes: [],
      primaryGroupNumber: this.props.modalType.data.groupNumber || '',
      secondaryPlan: this.props.modalType.data.secondaryPayerName || '',
      secondaryPlanName: this.props.modalType.data.secondaryPlanName || '',
      secondaryMemberId: this.props.modalType.data.secondaryMemberId || '',
      secondaryGroupNumber: this.props.modalType.data.secondaryGroupNumber || '',
      secondaryPolicyType: '',
      searchedSecondaryPayerNames: [],
      searchedSecondaryPolicyTypes: [],
      insuranceEffectiveDate: '',
      searchedPolicyTypes: [],
      groupNumber: this.props.modalType.data.groupNumber || '',
      apiPatientFound: false,
      apiSearchRelaxed: false,
      isInitialLoadComplete: false,
      createTaskAfterUpload: localStorage.createTaskAfterUpload === 'true',
      // Task creation mode for EMA radio buttons - 'auto', 'custom', or 'none'
      taskCreationMode: 'none',
      // Track if user has explicitly selected a task mode (to avoid overriding their choice)
      hasUserSelectedTaskMode: false,
      // The matched template for current category (or null)
      matchedTaskTemplate: null,
      assignTo: undefined,
      searchedTemplates: null,
      emaTaskTemplates: null,
      searchedNames: null,
      task: '',
      searchedTasks: '',
      taskDetails: '',
      dueDate: '',
      dateOfService: '',
      documentType: '',
      taskDetailsSearchOptions: null,
      notes: taskCreatedByStamp(),
      priority: 'Normal',
      facilityName: "",
      facilityNames: [],
      searchedFacilities: [],
      icd10Codes: this.props.modalType.data.icdCodes || this.props.modalType.data.icd10Codes || [],
      marketingRep: this.props.modalType.data.marketingRep || '',
      primaryPayerAddress: this.props.modalType.data.primaryInsurancePoBox || '',
      primaryPayerState: this.props.modalType.data.primaryInsuranceState || '',
      secondaryPayerAddress: this.props.modalType.data.secondaryInsurancePoBox || '',
      secondaryPayerState: this.props.modalType.data.secondaryInsuranceState || '',
      selectedEmrPatientId: null, // Store selected patient ID from Tebra/EMA search
      // Secondary insurance is collapsed by default; revealed via the toggle, or
      // shown automatically when AI extracted secondary data (see secondaryInsurance).
      showSecondaryInsurance: false,
      // Initialize authorization fields from AI-extracted data.
      // AI dates come in YYYY-MM-DD format, convert to MM/DD/YYYY for DatePicker.
      // Authorization is collapsed by default and auto-expands only when AI
      // extracted an authorization number.
      addAuthorization: !!(this.props.modalType.data.authorizationNumber),
      authorizationType: this.props.modalType.data.authorizationNumber ? 'Authorization' : '',
      authorizationNumber: this.props.modalType.data.authorizationNumber || '',
      authorizationDescription: this.props.modalType.data.authorizationDescription || '',
      authorizationActiveStatus: true,
      authorizationUnlimitedVisits: !this.props.modalType.data.authorizationNumberOfVisits && !!(this.props.modalType.data.authorizationFromDate && this.props.modalType.data.authorizationToDate),
      authorizationNumberOfVisits: this.props.modalType.data.authorizationNumberOfVisits ? String(this.props.modalType.data.authorizationNumberOfVisits) : '',
      authorizationFromDate: this.props.modalType.data.authorizationFromDate ? moment(this.props.modalType.data.authorizationFromDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : '',
      authorizationToDate: this.props.modalType.data.authorizationToDate ? moment(this.props.modalType.data.authorizationToDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : '',
      primaryPolicyTypeLocked: false,
      secondaryPolicyTypeLocked: false,
      authorizationNote: '',
      authorizationReferringProvider: '',
      canExtractAuthorization: false,
      hasPlanNameFieldEnabled: false,
    };
    if (
      !this.state.fileName
      && this.props.selectedRecord[0]
      && this.props.selectedRecord[0].document_title
    ) {
      this.state.fileName = this.props.selectedRecord[0].document_title;
    } else if (this.props.modalType.data.fileName) {
      this.state.fileName = this.props.modalType.data.fileName;
    } else {
      this.state.fileName = '';
    }

    this.state.searchedTasks = taskOptions;
    this.state.taskDetailsSearchOptions = taskDetailsOptions;
    // Sort the policyTypes array alphabetically
    const sortedPolicyTypes = policyTypes.sort((a, b) => a.localeCompare(b));

    this.state.policyTypeOptions = sortedPolicyTypes;
    this.state.searchedPrimaryPolicyTypes = sortedPolicyTypes;
    this.state.searchedSecondaryPolicyTypes = sortedPolicyTypes;
    this.formRef = React.createRef();
  }

  handleTaskTemplateChange = (e) => {
    // if user clears out selection, we will reload the list of templates to choose from.
    if (!e) {
      this.handleTaskTemplateSearch();
      const stamp = taskCreatedByStamp();
      this.setState({
        notes: stamp,
        priority: undefined,
        taskDetails: undefined,
        task: undefined,
        assignTo: undefined,
      }, () => {
        this.formRef.current.setFieldsValue({
          notes: stamp,
          assignTo: undefined,
          priority: undefined,
          taskDetails: undefined,
          task: undefined,
        });
      });
      return;
    }
    const index = this.state.emaTaskTemplates.map((template) => template.id).indexOf(e);
    const selectedTemplate = this.state.emaTaskTemplates[index];
    const assigtoArray = selectedTemplate.assign_to_as_array;
    if (assigtoArray.includes('Ordering Provider')) {
      assigtoArray.splice(assigtoArray.indexOf('Ordering Provider'), 1);
    }
    const newNotes = buildTaskTemplateNotes(selectedTemplate.notes);
    this.setState({
      priority: selectedTemplate.priority,
      taskDetails: selectedTemplate.task_details,
      task: selectedTemplate.task_type,
      assignTo: assigtoArray,
      notes: newNotes,
    }, () => {
      // Get form instance from ref
      this.formRef.current.setFieldsValue({
        priority: selectedTemplate.priority,
        taskDetails: selectedTemplate.task_details,
        task: selectedTemplate.task_type,
        assignTo: assigtoArray,
        notes: newNotes,
      });
    });
  };

  handleAssignToSearch = (e) => {
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const allProviders = autoUploadEmr === 'Kareo'
      ? (this.props.allKareoTaskProviders || [])
      : (this.props.allEmaTaskProviders || []);

    if (e) {
      const filtered = allProviders.filter((provider) => {
        const providerName = typeof provider === 'string' ? provider : provider?.name;
        return providerName && providerName.toLowerCase().includes(e.toLowerCase());
      });
      this.setState({ searchedNames: filtered });
    } else {
      this.setState({ searchedNames: allProviders });
    }
  };

  handleTaskTemplateSearch = (e) => {
    const unfilteredObjects = this.state.emaTaskTemplates;
    if (e) {
      const filtered = this.state.emaTaskTemplates.map((template) => template.task_name).filter((option) => (option.toLowerCase().includes(e.toLowerCase())));
      const filteredObject = [];
      filtered.forEach((template) => {
        const index = this.state.emaTaskTemplates.map((t) => t.task_name).indexOf(template);
        filteredObject.push(this.state.emaTaskTemplates[index]);
      });
      this.setState({ searchedTemplates: filteredObject });
    } else {
      this.setState({ searchedTemplates: unfilteredObjects });
    }
  };

  handleFacilitySearch = (value) => {
    if (value) {
      const filtered = this.state.facilityNames.filter(facility => 
        facility.name.toLowerCase().includes(value.toLowerCase())
      );
      this.setState({ searchedFacilities: filtered });
    } else {
      this.setState({ searchedFacilities: this.state.facilityNames });
    }
  };

  handleFacilityChange = (value) => {
    if (!value) {
      this.setState({ facilityName: '', facilityId: null });
      return;
    }
    const selectedFacility = this.state.searchedFacilities.find(facility => facility.name === value);
    this.setState({ facilityName: selectedFacility.name, facilityId: selectedFacility.id });
  };

  handleCreateTaskAfterUpload = async (e) => {
    if (e.target.checked) {
      localStorage.setItem('createTaskAfterUpload', true);
      this.setState({
        createTaskAfterUpload: true
      }, () => {
        requestAnimationFrame(() => {
          const taskTemplatesSection = document.querySelector('[id="taskTemplates"]');
          if (taskTemplatesSection) {
            taskTemplatesSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      });
    } else {
      localStorage.removeItem('createTaskAfterUpload');
      this.setState({ createTaskAfterUpload: false });
    }
  }

  // Find a task template that matches the given document category
  findTemplateForCategory = (categoryName) => {
    const { emaTaskTemplates } = this.state;
    if (!emaTaskTemplates || emaTaskTemplates.length === 0 || !categoryName) return null;
    return emaTaskTemplates.find(t => t.emr_category === categoryName) || null;
  };

  handleTaskSearch = (e) => {
    if (e) {
      const filtered = this.taskOptions.filter(task => task.toLowerCase().includes(e.toLowerCase()));
      this.setState({ searchedTasks: filtered });
    } else {
      this.setState({ searchedTasks: this.taskOptions });
    }
  };

  handleSelectionChange = (e, variable) => {
    this.setState({ [variable]: e });
  };

  handleNotesChange = (e) => {
    this.setState({ notes: e.target.value });
  }

  handleSearchTaskDetails = (e) => {
    this.setState({ taskDetails: e });
    const filteredOptions = taskDetailsOptions.filter(option => (
      option.toLowerCase().includes(e.toLowerCase())));
    this.setState({ taskDetailsSearchOptions: filteredOptions });
  };

  handleDueDateChange = (_, dateString) => {
    this.setState({ dueDate: dateString });
  };

  isKareoEmr = () => localStorage.getItem('m8_auto_upload_emr') === 'Kareo';

  handleICD10CodesChange = (value) => {
    this.setState({ icd10Codes: value });
  };

  handleMarketingRepChange = (value) => {
    this.setState({ marketingRep: value });
  };

  renderAssignToSelect() {
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const noProviderMessage = 'Contact your Admin to add EMR Providers';
    const providers = autoUploadEmr === 'Kareo'
      ? this.props.allKareoTaskProviders
      : this.props.allEmaTaskProviders;
    const noProvidersAvailable = !providers || providers.length === 0;
    return (
      <Form.Item
        name="assignTo"
        label={noProvidersAvailable
          ? (
            <div>
              Assign to EMR Staff or Staff Group
              <Tooltip title="Please contact your Admin to add Providers">
                <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} />
              </Tooltip>
            </div>
          ) : (
            <div>
              Assign to EMR Staff or Staff Group
            </div>
          )}
        style={{ flex: 1 }}
      >
        <Select
          mode={autoUploadEmr === 'EMA' ? 'multiple' : undefined}
          showSearch
          placeholder="Select a provider"
          optionFilterProp="children"
          onChange={e => this.handleSelectionChange(e, 'assignTo')}
          onSearch={this.handleAssignToSearch}
          filterOption={false}
          value={this.state.assignTo}
          allowClear
          notFoundContent={noProviderMessage}
        >
          {this.state.searchedNames && this.state.searchedNames.map((e, index) => {
            const providerName = typeof e === 'string' ? e : e?.name;
            return (
              <Select.Option key={providerName + index} value={providerName}>
                {providerName}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>
    );
  }

  getEmaApiPatients = async () => {
    this.setState({
      apiPatientsList: [],
      apiPatientFound: false,
      apiSearchRelaxed: false,
    });

    if (this.state.patientFirstName && this.state.patientLastName && this.state.patientDOB) {
      const query = `?first_name=${encodeURIComponent(this.state.patientFirstName)}&middle_name=${encodeURIComponent(this.state.patientMiddleName)}&last_name=${encodeURIComponent(this.state.patientLastName)}&dob=${encodeURIComponent(this.state.patientDOB)}`;
      await this.props.getEmaPatients(query);
      if (this.props.apiError) {
        this.setState({
          apiPatientFound: false,
        });
        return;
      }
      this.setState({
        apiPatientsList: this.props.apiPatients.patients,
        apiPatientFound: this.props.apiPatients.patients.length > 0,
        // True when the matches came from a DOB-anchored fallback (name didn't
        // match exactly) — used to soften the duplicate-patient warning copy
        apiSearchRelaxed: Boolean(this.props.apiPatients.relaxed),
      });
    }
  };

  getTebraApiPatients = async () => {
    this.setState({
      apiPatientsList: [],
      apiPatientFound: false,
    });

    if (this.state.patientFirstName && this.state.patientLastName && this.state.patientDOB) {
      const query = `?first_name=${encodeURIComponent(this.state.patientFirstName)}&middle_name=${encodeURIComponent(this.state.patientMiddleName)}&last_name=${encodeURIComponent(this.state.patientLastName)}&dob=${encodeURIComponent(this.state.patientDOB)}`;
      await this.props.getTebraPatients(query);

      if (this.props.apiError) {
        this.setState({
          apiPatientFound: false,
        });
        return;
      }
      const patients = this.props.apiPatients?.patients || [];
      this.setState({
        apiPatientsList: patients,
        apiPatientFound: patients.length > 0,
      });
    }
  };

  async componentDidMount() {
    const {
      getPayerNames,
      getDocumentCategories,
      getTaskTemplatesByQuery,
      getEmaTaskProviders,
      getKareoTaskProviders,
      getMarketingRepresentatives,
      modalType: {
        data: { callerName, receivedAt },
      },
    } = this.props;
    const { primaryPlan, secondaryPlan, sendToken } = this.state;
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const nymblTenantId = localStorage.getItem('m8_nymbl_tenant_id');
    const { patientName, patientFirstName, patientLastName } = this.state;
    const shouldParsePatientName = patientName
    && patientFirstName === ''
    && patientLastName === '';
    await getDocumentCategories();
    await getPayerNames();

    // Fetch appropriate providers based on EMR type
    if (autoUploadEmr === 'Kareo') {
      await getKareoTaskProviders(sendToken);
    } else {
      await getEmaTaskProviders(sendToken);
    }

    await getMarketingRepresentatives();

    // Get provider names from the appropriate source
    const allProviders = autoUploadEmr === 'Kareo'
      ? (this.props.allKareoTaskProviders || [])
      : (this.props.allEmaTaskProviders || []);

    this.setState({
      searchedNames: allProviders,
    });

    if (allProviders.length === 1) {
      let newAssignTo = allProviders[0];
      const providerName = typeof newAssignTo === 'string' ? newAssignTo : newAssignTo?.name;
      if (autoUploadEmr === 'EMA') {
        // Set assign to as array
        newAssignTo = [providerName];
      } else {
        newAssignTo = providerName;
      }
      this.setState({ assignTo: newAssignTo });
    }

    const query = `?email=${localStorage.getItem('m8_uid')}`;
    await getTaskTemplatesByQuery(query);
    this.setState({
      emaTaskTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
      searchedTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
    });

    // Check if initial documentCategory has a matching template
    const { documentCategory } = this.state;
    const templates = this.props.allEmaTaskTemplates.ema_task_templates || [];
    if (documentCategory && templates.length > 0) {
      const matchedTemplate = templates.find(t => t.emr_category === documentCategory);
      if (matchedTemplate) {
        this.setState({
          matchedTaskTemplate: matchedTemplate,
          taskCreationMode: 'auto'
        });
      }
    }

    // Filter payerNames based on the default primaryPlan
    if (primaryPlan) {
      const filteredPrimary = this.props.allPayerNames.filter(payer => 
        payer.toLowerCase().includes(primaryPlan.toLowerCase())
      );
      if (filteredPrimary.length === 1) {
        this.setState({ primaryPlan: filteredPrimary[0] });
      } else if (filteredPrimary.length > 0) {
        this.setState({ searchedPrimaryPayerNames: filteredPrimary, searchedPrimaryPayerNames: this.props.allPayerNames });
      } else {
        this.setState({ searchedPrimaryPayerNames: this.props.allPayerNames });
      }
    } else {
      this.setState({ searchedPrimaryPayerNames: this.props.allPayerNames });
    }

    if (secondaryPlan) {
      const filteredSecondary = this.props.allPayerNames.filter(payer => 
        payer.toLowerCase().includes(secondaryPlan.toLowerCase())
      );
      if (filteredSecondary.length === 1) {
        this.setState({ secondaryPlan: filteredSecondary[0], searchedSecondaryPayerNames: this.props.allPayerNames });
      } else if (filteredSecondary.length > 0) {
        this.setState({ searchedSecondaryPayerNames: filteredSecondary });
      } else {
        this.setState({ searchedSecondaryPayerNames: this.props.allPayerNames });
      }
    } else {
      this.setState({ searchedSecondaryPayerNames: this.props.allPayerNames });
    }

    if (this.state.populateInsurance && (this.state.secondaryPlan || this.state.secondaryPlanName || this.state.secondaryMemberId || this.state.secondaryGroupNumber)) {
      this.setState({ secondaryInsurance: true });
    }

    // Auto-populate insurance fields if data is available from ReferralManagement and payers exist
    if ((this.props.modalType.data.payerName || this.props.modalType.data.primary_plan || this.props.modalType.data.primaryMemberId) && this.props.allPayerNames && this.props.allPayerNames.length > 0) {
      this.setState({ populateInsurance: true });
    }
    if (this.props.modalType.data.secondaryPayerName
      || this.props.modalType.data.secondaryPlanName
      || this.props.modalType.data.secondaryMemberId
      || this.props.modalType.data.secondaryGroupNumber) {
      this.setState({ secondaryInsurance: true });
    }

    // Set initial policy type to Commercial - Other if there are payer names and populateInsurance is checked
    if (this.state.populateInsurance && this.props.allPayerNames && this.props.allPayerNames.length > 0 && autoUploadEmr === 'EMA' ) {
      this.setState({ primaryPolicyType: 'Commercial - Other', secondaryPolicyType: 'Commercial - Other' });
    }

    const org = localStorage.m8_org;
    // Frontier Dermatology does not want to below filename format
    if (!this.state.fileName && org !== 'Frontier Dermatology') {
      const receivedAtVal = receivedAt || this.props.receivedAt;
      const callerNameVal = callerName || this.props.callerName;
      this.setState({ fileName: (receivedAtVal && callerNameVal) ? `${receivedAtVal.replace(/ /g, '_')}_${callerNameVal.replace(/ /g, '_')}` : '' });
    }
    this.setState({
      documentCategories: this.props.allDocumentCategories.categories,
    });

    // Parse patient_name into first, middle, and last name fields if split fields are empty
    if (shouldParsePatientName) {
      // Handle edge case where the patient_name has 2 surnames (this is common for Spanish names)
      if (
        patientName.includes(',') &&
        patientName.split(',')[0].trim().includes(' ') &&
        patientName.split(',')[0].trim().split(' ').length > 1
      ) {
        const parsedNames = this.parseName(patientName);
        this.setState({
          patientFirstName: parsedNames.firstName,
          patientLastName: parsedNames.lastName,
        });
      } else {
        this.setState({
          patientFirstName: parseFullName(patientName).first,
          patientMiddleName: parseFullName(patientName).middle,
          patientLastName: parseFullName(patientName).last,
        });
      }
    }
    const orgName = localStorage.m8_org;
    if (orgName === 'PrePost+') {
      this.setState({ practiceName: 'PrePost+ Clarksville' });
    }

    setTimeout(() => {
      if (this.formRef.current) {
        const fieldsToValidate = [
          'primaryGroupNumber', 
          'secondaryGroupNumber', 
          'primaryMemberId', 
          'secondaryMemberId',
        ].filter(field => {
          const fieldValue = this.state[field];
          return fieldValue && fieldValue.trim() !== '';
        });

        if (fieldsToValidate.length > 0) {
          this.formRef.current.validateFields(fieldsToValidate);
        }
      }
    }, 0);

    // If user has EMA API credentials, get patients from EMA API
    if (this.state.apiCredentials && autoUploadEmr === 'EMA') {
      try {
        const response = await axios.get('/api/v1/emr_facilities');
        const res = await response.data;

        this.setState({
          facilityNames: res.facilities,
          searchedFacilities: res.facilities
        });
        
      } catch (error) {
        console.error('Error fetching facilities:', error);
      }
    }

    // Check feature flags
    const canExtractAuthorization = await verifyFeatureAccess('extract_authorization_details');
    const hasPlanNameFieldEnabled = await verifyFeatureAccess('ema_insurance_plan_name_field');
    this.setState({
      isInitialLoadComplete: true,
      canExtractAuthorization,
      hasPlanNameFieldEnabled,
    });
  };

  // Handle timing issues when templates load after initial render
  componentDidUpdate(prevProps) {
    const { allEmaTaskTemplates } = this.props;
    const { documentCategory, matchedTaskTemplate, emaTaskTemplates, hasUserSelectedTaskMode } = this.state;

    // Check if templates just loaded (weren't there before, are there now)
    const prevTemplates = prevProps.allEmaTaskTemplates?.ema_task_templates;
    const currTemplates = allEmaTaskTemplates?.ema_task_templates;
    const templatesJustLoaded = (
      (!prevTemplates || prevTemplates.length === 0) &&
      (currTemplates && currTemplates.length > 0)
    );

    // Guard: Only process if templates just loaded AND we haven't already stored them
    const alreadyProcessed = emaTaskTemplates?.length > 0;

    if (templatesJustLoaded && !alreadyProcessed) {
      // Always store templates in state so findTemplateForCategory works later
      const newState = {
        emaTaskTemplates: currTemplates,
        searchedTemplates: currTemplates,
      };

      // If we have a category, try to find a matching template
      if (documentCategory && !matchedTaskTemplate) {
        const template = currTemplates.find(t => t.emr_category === documentCategory);
        if (template) {
          newState.matchedTaskTemplate = template;
          // Only auto-select 'auto' mode if user hasn't already made an explicit selection
          if (!hasUserSelectedTaskMode) {
            newState.taskCreationMode = 'auto';
          }
        }
      }

      this.setState(newState);
    }
  }

  triggerChartCreation = async (formattedDOB) => {
    const { hideModal, triggerAutoUpload, allPayerNames } = this.props;
    const {
      sendToken,
      fileName,
      documentCategory,
      patientName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      secondaryPhone,
      patientPhoneNumber,
      patientGender,
      patientAddress,
      patientZipCode,
      patientCity,
      practitionerName,
      receiverType,
      patientEmail,
      patientState,
      referringProvider,
      providerOffice,
      primaryMemberId,
      primaryPlan,
      primaryPlanName,
      practiceName,
      primaryPolicyType,
      primaryGroupNumber,
      secondaryInsurance,
      secondaryPlan,
      secondaryPlanName,
      secondaryMemberId,
      secondaryGroupNumber,
      referringProviderId,
      createTaskAfterUpload,
      taskCreationMode,
      notes,
      task,
      taskDetails,
      priority,
      dueDate,
      assignTo,
      facilityName,
      marketingRep,
      insuranceEffectiveDate,
      selectedEmrPatientId,
      addAuthorization,
      authorizationType,
      authorizationNumber,
      authorizationDescription,
      authorizationActiveStatus,
      authorizationUnlimitedVisits,
      authorizationNumberOfVisits,
      authorizationFromDate,
      authorizationToDate,
      canExtractAuthorization,
    } = this.state;

    // Only send authorization data if feature flag is enabled AND user checked the checkbox
    const shouldSendAuthorization = addAuthorization && canExtractAuthorization;

    let formattedDueDate = '';
    const finalizedMarketingRep = this.props.allMarketingRepresentatives?.includes(marketingRep) ? marketingRep : null;

    if (createTaskAfterUpload) {
      if (dueDate !== '') {
        const splitDate = dueDate.split('/');
        formattedDueDate = `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}`;
      }
    }

    const updateState = () => {
      return new Promise(resolve => {
        if (!secondaryInsurance) {
          if (allPayerNames.includes(secondaryPlan) && secondaryGroupNumber && secondaryMemberId) {
            resolve();
          } else {
            this.setState({
              secondaryPlan: '',
              secondaryPlanName: '',
              secondaryMemberId: '',
              secondaryGroupNumber: '',
              secondaryPolicyType: ''
            }, resolve);
          }
        } else {
          resolve();
        }
      });
    };

    await updateState();

    const uploadResult = await triggerAutoUpload({
      sendToken,
      receiverType,
      documentCategory,
      fileName,
      patientName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      secondaryPhone,
      patientDOB: formattedDOB,
      patientPhoneNumber,
      patientGender,
      patientAddress,
      patientZipCode,
      patientCity,
      practitionerName,
      chartCreationEnabled: true,
      patientEmail,
      patientState,
      referringProvider,
      providerOffice,
      primaryMemberId,
      primaryPlan,
      primaryPlanName: this.state.hasPlanNameFieldEnabled ? primaryPlanName : '',
      practiceName,
      primaryPolicyType,
      primaryGroupNumber,
      assignTo,
      createTaskAfterUpload,
      taskCreationMode,
      task,
      taskDetails,
      priority,
      dueDate: formattedDueDate,
      notes,
      secondaryPlan: this.state.secondaryPlan,
      secondaryPlanName: this.state.hasPlanNameFieldEnabled ? secondaryPlanName : '',
      secondaryMemberId: this.state.secondaryMemberId,
      secondaryPolicyType: this.state.secondaryPolicyType,
      secondaryGroupNumber: this.state.secondaryGroupNumber,
      referringProviderId,
      facilityName,
      marketingRep: finalizedMarketingRep,
      icd10Codes: this.state.icd10Codes,
      caseName: this.state.caseName,
      primaryPayerAddress: this.state.primaryPayerAddress,
      primaryPayerState: this.state.primaryPayerState,
      secondaryPayerAddress: this.state.secondaryPayerAddress,
      secondaryPayerState: this.state.secondaryPayerState,
      insuranceEffectiveDate,
      emrPatientId: selectedEmrPatientId,
      authorizationType: shouldSendAuthorization ? authorizationType : '',
      authorizationNumber: shouldSendAuthorization ? authorizationNumber : '',
      authorizationDescription: shouldSendAuthorization ? authorizationDescription : '',
      authorizationActiveStatus: shouldSendAuthorization ? authorizationActiveStatus : false,
      authorizationUnlimitedVisits: shouldSendAuthorization ? authorizationUnlimitedVisits : false,
      authorizationNumberOfVisits: shouldSendAuthorization ? authorizationNumberOfVisits : '',
      authorizationFromDate: shouldSendAuthorization ? authorizationFromDate : '',
      authorizationToDate: shouldSendAuthorization ? authorizationToDate : '',
      authorizationNote: shouldSendAuthorization ? this.state.authorizationNote : '',
      authorizationReferringProvider: shouldSendAuthorization ? this.state.authorizationReferringProvider : '',
    });
    const { error, errorMessage } = this.props;
    hideModal();
    if (!error) {
      // ENG-3012 TIFF deferral: the reply merge runs for every Reply parent
      // BEFORE the endpoint branches on upload_type, so chart_creation gets the
      // same partial-filing warning Send to Chart does. Without this the
      // excluded TIFF is silent here.
      const tiffWarnings = extractReplyTiffWarnings(uploadResult);
      if (tiffWarnings.length > 0) {
        showReplyTiffWarningModal({ warnings: tiffWarnings, workflow: 'legacy_create_chart' });
      } else {
        message.success({
          content: 'Patient chart will be created shortly. We will notify you of any issues via email.',
        });
      }
      // Refresh the current records view so the row shows the in-progress chart status
      // (chart_in_progress) without a manual refresh.
      this.props.refreshCurrentRecords?.();
    } else {
      message.error({
        content: errorMessage,
      });
    }
  }

  // Bound to an existing EMR chart → file the document to it instead of creating a
  // new patient. Routes through the send-to-chart path (no create_chart_enabled), so
  // the backend does not require gender and the worker associates to the existing
  // chart via the patient id/MRN. Mirrors the AutoUpload (Send to Chart) submission.
  fileToExistingChart = async (formattedDOB) => {
    const { hideModal, triggerAutoUpload, triggerAutoUploadAndTaskCreation } = this.props;
    const {
      sendToken, receiverType, documentCategory, fileName,
      patientName, patientFirstName, patientMiddleName, patientLastName,
      selectedEmrPatientId, taskCreationMode, task, taskDetails, assignTo,
      priority, dueDate, notes, facilityName,
      addAuthorization, authorizationType, authorizationNumber, authorizationDescription,
      authorizationActiveStatus, authorizationUnlimitedVisits, authorizationNumberOfVisits,
      authorizationFromDate, authorizationToDate, authorizationNote, authorizationReferringProvider,
      canExtractAuthorization,
    } = this.state;

    const shouldSendAuthorization = addAuthorization && canExtractAuthorization;
    let formattedDueDate = '';
    if (dueDate) {
      const s = dueDate.split('/');
      formattedDueDate = `${s[2]}-${s[0].padStart(2, '0')}-${s[1].padStart(2, '0')}`;
    }
    const authPayload = {
      authorizationType: shouldSendAuthorization ? authorizationType : '',
      authorizationNumber: shouldSendAuthorization ? authorizationNumber : '',
      authorizationDescription: shouldSendAuthorization ? authorizationDescription : '',
      authorizationActiveStatus: shouldSendAuthorization ? authorizationActiveStatus : false,
      authorizationUnlimitedVisits: shouldSendAuthorization ? authorizationUnlimitedVisits : false,
      authorizationNumberOfVisits: shouldSendAuthorization ? authorizationNumberOfVisits : '',
      authorizationFromDate: shouldSendAuthorization ? authorizationFromDate : '',
      authorizationToDate: shouldSendAuthorization ? authorizationToDate : '',
      authorizationNote: shouldSendAuthorization ? authorizationNote : '',
      authorizationReferringProvider: shouldSendAuthorization ? authorizationReferringProvider : '',
    };

    // Only the custom task mode maps to the send-to-chart path (upload_and_task).
    // 'auto' (template) is a create-chart-only capability, so when filing to an
    // existing chart we upload the document without an auto-template task.
    const wantsCustomTask = taskCreationMode === 'custom' && !!task && !!taskDetails
      && Array.isArray(assignTo) && assignTo.length > 0;

    // Chart-identity contract (see PR review, "selected chart identity is overloaded"):
    // selectedEmrPatientId holds the EMA chart MRN for EMA — the only EMR enabled on
    // this redesign — and would hold the patient ID for Tebra/Kareo. Both branches
    // below are correct for EMA because the EMA worker resolves the chart by MRN.
    // The Tebra/Kareo upload-and-task path is NOT correct yet: that EMR needs the
    // patient ID sent as emr_patient_id, but triggerAutoUploadAndTaskCreation does
    // not forward emr_patient_id (only triggerAutoUpload does). Reworking this into an
    // explicit { emr, patientId, mrn, pmsId } selected-chart object + one payload
    // builder (plus action/backend emr_patient_id wiring) is deferred until the
    // dormant Tebra/Kareo redesign path is productionized.
    let boundUploadResult;
    if (wantsCustomTask) {
      boundUploadResult = await triggerAutoUploadAndTaskCreation({
        sendToken,
        receiverType,
        documentCategory,
        fileName,
        patientFirstName,
        patientMiddleName,
        patientLastName,
        patientDOB: formattedDOB,
        patientMrn: selectedEmrPatientId,
        assignTo,
        task,
        taskDetails,
        dueDate: formattedDueDate,
        priority,
        notes,
        facilityName,
        ...authPayload,
      });
    } else {
      boundUploadResult = await triggerAutoUpload({
        sendToken,
        receiverType,
        documentCategory,
        fileName,
        patientName,
        patientFirstName,
        patientMiddleName,
        patientLastName,
        patientDOB: formattedDOB,
        patientMrn: selectedEmrPatientId,
        emrPatientId: selectedEmrPatientId,
        chartCreationEnabled: false,
        ...authPayload,
      });
    }

    const { error, errorMessage } = this.props;
    hideModal();
    if (!error) {
      // ENG-3012 TIFF deferral: both branches above file a document, so a reply
      // whose visible TIFFs were excluded must say so instead of claiming a
      // clean success.
      const tiffWarnings = extractReplyTiffWarnings(boundUploadResult);
      if (tiffWarnings.length > 0) {
        showReplyTiffWarningModal({
          warnings: tiffWarnings,
          workflow: wantsCustomTask ? 'legacy_bound_chart_upload_and_task' : 'legacy_bound_chart_upload',
        });
      } else {
        message.success({
          content: 'Document will be filed to the existing chart shortly. We will notify you of any issues via email.',
        });
      }
      // Refresh the current records view so the row shows the in-progress status
      // (auto_upload_in_progress) without a manual refresh.
      this.props.refreshCurrentRecords?.();
    } else {
      message.error({ content: errorMessage });
    }
  };

  // Guards against double-click firing two chart-creation/upload requests. The flag is
  // an instance property (not state) so the re-entry check is synchronous within a single
  // click burst, before any await yields control back to a second click. preventDefault
  // stays in the wrapper so it also suppresses the form's onFinish on a blocked click.
  handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      await this.performSubmit();
    } finally {
      this.isSubmitting = false;
    }
  };

  performSubmit = async () => {
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    const { patientDOB } = this.state;
    const splitDob = patientDOB.split('/');
    const formattedDOB = `${splitDob[2]}-${splitDob[0].padStart(2, '0')}-${splitDob[1].padStart(2, '0')}`;

    // A bound existing chart files to that chart (send-to-chart); the duplicate
    // check is unnecessary because the user already chose the chart.
    if (this.state.selectedEmrPatientId) {
      await this.fileToExistingChart(formattedDOB);
      return;
    }

    // Check for existing patients if API credentials are available
    if (this.state.apiCredentials) {
      if (autoUploadEmr === 'EMA') {
        await this.getEmaApiPatients();
      } else if (autoUploadEmr === 'Kareo') {
        await this.getTebraApiPatients();
      }
    }

    const {
      apiPatientFound,
      apiSearchRelaxed,
    } = this.state;

    if (apiPatientFound) {
      const isEma = autoUploadEmr === 'EMA';
      const exactMatchTitle = isEma ? 'Multiple Patients Found!' : 'Patient Already Exists!';
      confirm({
        title: apiSearchRelaxed ? 'Possible Duplicate Patient' : exactMatchTitle,
        content: apiSearchRelaxed
          ? `${autoUploadEmr} has patient(s) with the same date of birth under a similar or different name. This may be the same patient — creating a new chart could produce a duplicate. Would you like to send to chart instead?`
          : `It looks like this patient already exists in ${autoUploadEmr}. Would you like to send to chart instead?`,
        onOk: async () => {
          await this.triggerChartCreation(formattedDOB);
        },
        onCancel() {},
      });
    } else {
      await this.triggerChartCreation(formattedDOB);
    }
  };

  parseName(name) {
    let firstName = '';
    let lastName = '';
    const parts = name.split(',');
    lastName = parts[0].trim();
    firstName = parts[1].trim();

    // Handle edge cases for Spanish names with multiple surnames
    if (lastName.split(' ').length > 1 && !lastName.includes('-')) {
      const lastNames = lastName.split(' ');
      lastName = `${lastNames[0]} ${lastNames[1]}`;
    }

    return { firstName, lastName };
  };

  handlePatientSelection(record) {
    if (!record || !record.name || !record.dob) {
      return;
    }

    const { name: fullName, dob, id, mrn = id } = record;
    const patientName = fullName.trim();
    const formattedDOB = moment(dob.trim()).format('MM/DD/YYYY');
    const { first, middle, last } = parseFullName(patientName);

    this.setState({
      patientFirstName: first,
      patientMiddleName: middle,
      patientLastName: last,
      patientName,
      patientDOB: formattedDOB,
      open: false,
      // Overloaded: EMA MRN (live) or Tebra patient ID. See the chart-identity
      // contract note in fileToExistingChart for the deferred Tebra/Kareo rework.
      selectedEmrPatientId: id || mrn,
    }, () => {
      // Sync form fields with updated state
      if (this.formRef.current) {
        this.formRef.current.setFieldsValue({
          form_patient_first_name: first,
          form_patient_middle_name: middle,
          form_patient_last_name: last,
          patientDOB: moment(formattedDOB, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']),
        });
      }
    });
  };

  render() {
    const {
      modalType: {
        data: { name },
      },
      isLoading,
      hideModal,
      allPayerNames,
    } = this.props;
    const { genderOptions } = this.state;
    const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    const nymblTenantId = localStorage.getItem('m8_nymbl_tenant_id');
    const isNymbl = autoUploadEmr === 'Nymbl';
    const isNymblWithTenant = isNymbl && nymblTenantId && nymblTenantId !== 'undefined';
    const isAdvancedMD = autoUploadEmr === 'AdvancedMD';
    const isEMA = autoUploadEmr === 'EMA';
    const isKareo = autoUploadEmr === 'Kareo';
    const customContentStyle = {};
    const sectionHeadingStyle = {
      display: 'block', fontWeight: 600, fontSize: '14px', margin: '8px 0 4px',
    };
    const {
      patientName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      secondaryPhone,
      patientDOB,
      patientPhoneNumber,
      patientGender,
      patientAddress,
      patientZipCode,
      patientCity,
      practitionerName,
      documentCategory,
      documentCategories,
      fileName,
      patientEmail,
      patientState,
      referringProvider,
      referringProviderId,
      providerOffice,
      primaryMemberId,
      primaryPlan,
      primaryPlanName,
      practiceName,
      primaryPolicyType,
      primaryGroupNumber,
      searchedPrimaryPayerNames,
      secondaryPolicyType,
      secondaryPlan,
      secondaryPlanName,
      secondaryMemberId,
      secondaryGroupNumber,
      searchedSecondaryPayerNames,
      populateInsurance,
      secondaryInsurance,
      insuranceEffectiveDate,
      isInitialLoadComplete,
      createTaskAfterUpload,
      taskCreationMode,
      matchedTaskTemplate,
      assignTo,
      task,
      taskDetails,
      apiCredentials,
      primaryPayerAddress,
      primaryPayerState,
      secondaryPayerAddress,
      secondaryPayerState,
    } = this.state;

    const canCreateTaskAfterUpload = isEMA || isKareo;
    const canUseTaskTemplates = isEMA;
    const taskCheckboxLabel = isKareo ? 'Create note after upload' : 'Create task after upload';
    const shouldShowPlanNameField = isEMA && populateInsurance && this.state.hasPlanNameFieldEnabled;

    const renderPlanNameSelect = (type, payer, planName) => {
      const planOptions = (this.props.payerPlanNamesMap || {})[payer] || [];
      const hasOptions = planOptions.length > 0;
      const getPlaceholder = () => {
        if (!payer) return "Select a payer first";
        if (!hasOptions) return "No plan names found for this payer.";
        return "Select plan name";
      };
      return (
      <div style={{ display: 'flex', gap: '10px' }}>
        <Form.Item
          label={<span>Plan Name <span style={{ color: 'red' }}>*</span></span>}
          style={{ flex: 1 }}
        >
          <Select
            showSearch
            allowClear={hasOptions}
            placeholder={getPlaceholder()}
            disabled={!payer || !hasOptions}
            value={planName || undefined}
            onChange={handlePlanNameChange(type)}
            optionFilterProp="children"
            style={{ width: '100%' }}
          >
            {planOptions.map(p => (
              <Select.Option key={p.name} value={p.name}>
                {p.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>
      );
    };

    const validatePhoneNumber = (rule, value) => {
      if (!value) {
        return Promise.reject('Please input your phone number!');
      }
      if (!validPhoneNumber(value)) {
        return Promise.reject('Please enter a valid phone number!');
      }
      return Promise.resolve();
    };

    const validateZipCode = (rule, value) => {
      if (!value) {
        return Promise.reject('Please input a zip code!');
      }
      if (!validZipCode(value)) {
        return Promise.reject('Please enter a valid zip code!');
      }
      return Promise.resolve();
    };

    const handleFieldChange = (e) => {
      const { name, value } = e.target;

      // Map AntD form field names to state variable names
      const fieldMapping = {
        form_primary_payer_address: 'primaryPayerAddress',
        form_primary_payer_state: 'primaryPayerState',
        form_primary_payer_name: 'primaryPlan',
        form_primary_policy_number: 'primaryMemberId',
        form_primary_payer_po_box: 'primaryPoBox',
        form_secondary_payer_address: 'secondaryPayerAddress',
        form_secondary_payer_state: 'secondaryPayerState',
        form_secondary_payer_name: 'secondaryPlan',
        form_secondary_policy_number: 'secondaryMemberId',
        form_secondary_payer_po_box: 'secondaryPayerAddress',
      };
      const stateFieldName = fieldMapping[name] || name;

      // Clear selected patient ID if user manually changes patient name fields.
      // A bound chart must never disagree with the visible name, so release the
      // binding on edit for both Tebra (id) and EMA (MRN).
      const patientNameFields = ['patientFirstName', 'patientMiddleName', 'patientLastName'];
      const shouldClearPatientId = patientNameFields.includes(stateFieldName) && (isKareo || isEMA);

      // Reset authorization when primary insurance fields are cleared (authorization requires insurance context)
      const insuranceFields = ['primaryPlan', 'primaryMemberId'];
      const shouldResetAuthorization = insuranceFields.includes(stateFieldName) && !value;

      this.setState({
        [stateFieldName]: value,
        ...(shouldClearPatientId && { selectedEmrPatientId: null }),
        ...(shouldResetAuthorization && {
          addAuthorization: false,
          authorizationType: '',
          authorizationNumber: '',
          authorizationDescription: '',
          authorizationActiveStatus: true,
          authorizationUnlimitedVisits: false,
          authorizationNumberOfVisits: '',
          authorizationFromDate: '',
          authorizationToDate: '',
          authorizationNote: '',
          authorizationReferringProvider: '',
        })
      }, () => {
        // Check if secondary insurance is populated
        if (this.state.secondaryGroupNumber || this.state.secondaryMemberId || this.state.secondaryPlanName) {
          this.setState({ secondaryInsurance: true });
        }
        if (!this.state.secondaryGroupNumber && !this.state.secondaryMemberId && !this.state.secondaryPlan && !this.state.secondaryPlanName) {
          this.setState({ secondaryInsurance: false });
        }
      });
    };

    const handleReferringProviderChange = (providerId, providerName) => {
      this.setState({
        referringProviderId: providerId,
        referringProvider: providerName,
      });
    };

    /**
     * @param {String} dateString format MM/DD/YYYY
     */
    const handleDateChange = (_, dateString) => {
      this.setState({
        patientDOB: dateString,
        // Release any bound chart on DOB edit (Tebra id or EMA MRN)
        ...((this.isKareoEmr() || isEMA) && { selectedEmrPatientId: null }),
      });
    };

    /**
     * @param {String} dateString format MM/DD/YYYY
     */
    const handleEffectiveDateChange = (_, dateString) => {
      this.setState({ insuranceEffectiveDate: dateString });
    };

    const handlePayerNameChange = (type) => (e) => {
      if (type === 'primary') {
        this.setState({
          primaryPlan: e,
          primaryPlanName: '',
          primaryPolicyTypeLocked: false,
        });
      } else {
        this.setState({
          secondaryPlan: e,
          secondaryPlanName: '',
          secondaryPolicyTypeLocked: false,
        });
      }
      if (type === 'secondary' && e) {
        this.setState({ secondaryInsurance: true });
      } else {
        this.setState({ secondaryInsurance: false });
      }
    };

    const handlePlanNameChange = (type) => (value) => {
      const { payerPlanNamesMap } = this.props;
      const payerKey = type === 'primary' ? 'primaryPlan' : 'secondaryPlan';
      const planNames = payerPlanNamesMap[this.state[payerKey]] || [];
      const selectedPlan = planNames.find(p => p.name === value);
      const prefix = type === 'primary' ? 'primary' : 'secondary';

      const updates = {
        [`${prefix}PlanName`]: value || '',
        [`${prefix}PolicyTypeLocked`]: !!(selectedPlan && selectedPlan.policy_type),
      };
      if (selectedPlan && selectedPlan.policy_type) {
        updates[`${prefix}PolicyType`] = selectedPlan.policy_type;
      } else {
        updates[`${prefix}PolicyType`] = '';
      }
      this.setState(updates);
    };

    const handlePrimaryPayerNameSearch = (e) => {
      if (e) {
        const filtered = allPayerNames.filter(payer => payer.toLowerCase().includes(e.toLowerCase()));
        this.setState({ searchedPrimaryPayerNames: filtered }); 
      } else {
        this.setState({ searchedPrimaryPayerNames: allPayerNames });
      }
    };

    const handleSecondaryPayerNameSearch = (e) => {
      if (e) {
        const filtered = allPayerNames.filter(payer => payer.toLowerCase().includes(e.toLowerCase()));
        this.setState({ searchedSecondaryPayerNames: filtered }); 
      } else {
        this.setState({ searchedSecondaryPayerNames: allPayerNames });
      }
    };

    const handlePolicyTypeChange = (type) => (e) => {
      if (type === 'primary') {
        this.setState({ primaryPolicyType: e });
      } else {
        this.setState({ secondaryPolicyType: e });
      }
    };

    const handlePopulateInsurance = async (e) => {
      if (e.target.checked) {
        localStorage.setItem('populateInsurance', 'true');
        // Set policy type to Commercial - Other if populateInsurance is checked
        this.setState({ 
          primaryPolicyType: 'Commercial - Other',
          secondaryPolicyType: 'Commercial - Other',
          populateInsurance: true
        }, () => {
          // Sync form fields with current state
          if (this.formRef.current) {
            this.formRef.current.setFieldsValue({
              primaryPlan: this.state.primaryPlan,
              primaryMemberId: this.state.primaryMemberId,
              primaryGroupNumber: this.state.primaryGroupNumber,
              secondaryPlan: this.state.secondaryPlan,
              secondaryMemberId: this.state.secondaryMemberId,
              secondaryGroupNumber: this.state.secondaryGroupNumber,
              effectiveDate: this.state.insuranceEffectiveDate ? moment(this.state.insuranceEffectiveDate, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']) : null,
            });
          }
          requestAnimationFrame(() => {
            const insuranceSection = document.querySelector('[name="primaryMemberId"]');
            if (insuranceSection) {
              insuranceSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
        });
      } else {
        localStorage.removeItem('populateInsurance');
        // Clear insurance fields and authorization if populateInsurance is unchecked
        this.setState({
          primaryPolicyType: '',
          primaryGroupNumber: '',
          primaryPlan: '',
          primaryPlanName: '',
          primaryMemberId: '',
          primaryPolicyTypeLocked: false,
          secondaryPolicyType: '',
          secondaryGroupNumber: '',
          secondaryPlan: '',
          secondaryPlanName: '',
          secondaryMemberId: '',
          secondaryPolicyTypeLocked: false,
          insuranceEffectiveDate: '',
          populateInsurance: false,
          // Reset authorization when insurance is unchecked
          addAuthorization: false,
          authorizationType: '',
          authorizationNumber: '',
          authorizationDescription: '',
          authorizationActiveStatus: true,
          authorizationUnlimitedVisits: false,
          authorizationNumberOfVisits: '',
          authorizationFromDate: '',
          authorizationToDate: '',
          authorizationNote: '',
          authorizationReferringProvider: '',
        });
      }
    };

    const isAssignToEmpty = Array.isArray(assignTo) ? assignTo.length === 0 : !assignTo;

    // For EMA: validate when taskCreationMode is 'custom'
    // For Kareo: validate when createTaskAfterUpload is true (checkbox)
    const taskValidationsFailed = (
      (isEMA && taskCreationMode === 'custom') || (isKareo && createTaskAfterUpload)
    ) && (
      isKareo
        ? isAssignToEmpty  // Kareo: only assignee required
        : (isAssignToEmpty || !task || !taskDetails)  // EMA: all required (catches '' and undefined)
    );

    // Single source of truth for EMA submit-gating. Each entry's `failed` flag both
    // disables Submit (any failed) and, via `message`, names the gap in the footer
    // (every failed one) — so the button state and the "what's left" copy can't drift.
    // Ordered to roughly follow the form top-to-bottom.
    const emaValidationItems = () => {
      const bound = this.state.selectedEmrPatientId !== null && this.state.selectedEmrPatientId !== undefined;
      const insuranceActive = populateInsurance && allPayerNames?.length > 0;
      const secondaryActive = populateInsurance && secondaryInsurance;
      const isAlnum = (value) => /^[a-zA-Z0-9]*$/.test(value);
      const primaryPlanOptions = (this.props.payerPlanNamesMap || {})[primaryPlan] || [];
      const primaryPlanNameMissing = this.state.hasPlanNameFieldEnabled
        && primaryPlanOptions.length > 0 && (!primaryPlanName || primaryPlanName.trim() === '');
      const secondaryPlanOptions = (this.props.payerPlanNamesMap || {})[secondaryPlan] || [];
      const secondaryPlanNameMissing = this.state.hasPlanNameFieldEnabled
        && secondaryPlanOptions.length > 0 && (!secondaryPlanName || secondaryPlanName.trim() === '');
      const authSectionVisible = populateInsurance && primaryPlan && primaryMemberId && this.state.canExtractAuthorization;
      const authFailed = authSectionVisible && this.state.addAuthorization && (
        !this.state.authorizationType
        || !this.state.authorizationNumber
        || !this.state.authorizationDescription
        || (this.state.authorizationUnlimitedVisits
          ? (!this.state.authorizationFromDate || !this.state.authorizationToDate)
          : !this.state.authorizationNumberOfVisits)
      );
      return [
        { failed: patientFirstName === '' || patientLastName === '', message: 'the patient name' },
        { failed: patientDOB === null || patientDOB === '', message: 'a date of birth' },
        { failed: !bound && (patientGender === null || patientGender === ''), message: 'a gender' },
        // New EMA charts require a phone number server-side (emr_uploads_controller
        // create-chart validation). Not needed when filing to an existing chart (bound
        // -> send-to-chart path, which skips that validation).
        { failed: !bound && (patientPhoneNumber === null || patientPhoneNumber === ''), message: 'a phone number' },
        { failed: Boolean(patientPhoneNumber && !validPhoneNumber(patientPhoneNumber)), message: 'a valid phone number' },
        { failed: !documentCategory, message: 'a document category' },
        { failed: insuranceActive && (!primaryPlan || !allPayerNames.includes(primaryPlan)), message: 'a primary payer' },
        { failed: insuranceActive && !primaryMemberId, message: 'a primary policy number' },
        { failed: Boolean(insuranceActive && primaryMemberId && !isAlnum(primaryMemberId)), message: 'a valid primary policy number' },
        { failed: Boolean(insuranceActive && primaryGroupNumber && !isAlnum(primaryGroupNumber)), message: 'a valid primary group number' },
        { failed: Boolean(insuranceActive && primaryPlanNameMissing), message: 'a primary plan name' },
        { failed: insuranceActive && !primaryPolicyType, message: 'a primary policy type' },
        { failed: secondaryActive && (!secondaryPlan || !allPayerNames.includes(secondaryPlan)), message: 'a secondary payer' },
        { failed: secondaryActive && !secondaryMemberId, message: 'a secondary policy number' },
        { failed: Boolean(secondaryActive && secondaryMemberId && !isAlnum(secondaryMemberId)), message: 'a valid secondary policy number' },
        { failed: Boolean(secondaryActive && secondaryGroupNumber && !isAlnum(secondaryGroupNumber)), message: 'a valid secondary group number' },
        { failed: Boolean(secondaryActive && secondaryPlanNameMissing), message: 'a secondary plan name' },
        { failed: secondaryActive && !secondaryPolicyType, message: 'a secondary policy type' },
        { failed: Boolean(apiCredentials && referringProvider && !referringProviderId), message: 'a referring provider from the dropdown' },
        { failed: authFailed, message: 'the authorization details' },
        { failed: taskValidationsFailed, message: 'the task details' },
      ];
    };

    const disableSubmitButton = () => {
      if (isEMA) {
        return emaValidationItems().some((item) => item.failed);
      }
      if (isKareo) {
        // Basic validations - first name, last name, DOB required for Kareo
        // Gender and phone required for Kareo without API (scraper needs them)
        const basicValidationsFailed = patientFirstName === ''
          || patientLastName === ''
          || patientDOB === null
          || patientDOB === ''
          || (!apiCredentials && (patientGender === '' || patientGender === null))
          || (!apiCredentials && (patientPhoneNumber === '' || patientPhoneNumber === null));

        // Insurance validations only apply when API credentials exist
        const insuranceValidationsFailed = apiCredentials && populateInsurance
          && allPayerNames?.length > 0
          && (!primaryPlan
            || !primaryMemberId
            || !allPayerNames.includes(primaryPlan)
            || (primaryMemberId && !/^[a-zA-Z0-9]*$/.test(primaryMemberId))
            || (primaryGroupNumber && !/^[a-zA-Z0-9]*$/.test(primaryGroupNumber))
          );

        // Secondary insurance validations only apply when API credentials exist
        const secondaryInsuranceValidationsFailed = apiCredentials && populateInsurance && secondaryInsurance && (
          !secondaryPlan
          || !secondaryMemberId
          || !allPayerNames.includes(secondaryPlan)
          || (secondaryMemberId && !/^[a-zA-Z0-9]*$/.test(secondaryMemberId))
          || (secondaryGroupNumber && !/^[a-zA-Z0-9]*$/.test(secondaryGroupNumber))
        );

        return basicValidationsFailed
          || taskValidationsFailed
          || insuranceValidationsFailed
          || secondaryInsuranceValidationsFailed;
      }
      if (isAdvancedMD) {
        // Basic validations
        const basicValidationsFailed = patientName === ''
          || patientDOB === null
          || patientDOB === ''
          || patientPhoneNumber === ''
          || !validSendEmail(patientEmail)
          || !validPhoneNumber(patientPhoneNumber)
          || patientGender === null
          || patientAddress === ''
          || patientZipCode === ''
          || !validZipCode(patientZipCode)
          || patientCity === ''
          || practitionerName === '';

        // Insurance validations
        const insuranceValidationsFailed = populateInsurance
          && allPayerNames?.length > 0
          && (!primaryPlan
            || !primaryMemberId
            || !allPayerNames.includes(primaryPlan)
            || insuranceEffectiveDate === null
            || insuranceEffectiveDate === ''
          );

        return basicValidationsFailed || insuranceValidationsFailed;
      }
      if (autoUploadEmr === 'Brightree') {
        // Basic validations
        const basicValidationsFailed = patientName === null
          || patientName === ''
          || patientDOB === null
          || patientDOB === ''
          || !patientGender;

        // Insurance validations - if they start adding primary insurance, all primary fields are required
        const primaryInsuranceStarted = primaryPlan || primaryMemberId || primaryPayerAddress || primaryPayerState;
        const primaryInsuranceValidationsFailed = primaryInsuranceStarted && (
          !primaryPlan
          || !primaryMemberId
          || !primaryPayerAddress
          || !primaryPayerState
        );

        // If they start adding secondary insurance, all secondary fields are required
        const secondaryInsuranceStarted = secondaryPlan || secondaryMemberId || secondaryPayerAddress || secondaryPayerState;
        const secondaryInsuranceValidationsFailed = secondaryInsuranceStarted && (
          !secondaryPlan
          || !secondaryMemberId
          || !secondaryPayerAddress
          || !secondaryPayerState
        );

        return basicValidationsFailed
          || primaryInsuranceValidationsFailed
          || secondaryInsuranceValidationsFailed;
      }
      if (autoUploadEmr === 'DrChrono') {
        return (
          patientName === null
          || patientDOB === null
          || patientDOB === ''
          || !patientGender
        );
      }
      if (isNymblWithTenant) {
        return (
          (!patientFirstName
          || !patientLastName
          || patientDOB === '')
        );
      }

      if (isNymbl) {
        return (
          patientName === null
          || patientName === ''
          || patientDOB === null
          || patientDOB === ''
        );
      }

      return (
        patientName === null
        || patientName === ''
        || patientDOB === null
        || patientDOB === ''
        || patientGender === null
        || patientPhoneNumber === null
        || !validPhoneNumber(patientPhoneNumber)
        || (autoUploadEmr === 'EMA' && !documentCategory)
        || taskValidationsFailed
      );
    };

    const noPayerNameMessage = 'Contact Admin to add payers';

    const validateAlphanumeric = (rule, value) => {
      if (!value) {
        return Promise.resolve();
      }
      const alphanumericRegex = /^[a-zA-Z0-9]*$/;
      if (!alphanumericRegex.test(value)) {
        return Promise.reject('Only alphanumeric characters are allowed!');
      }
      return Promise.resolve();
    };

    // ---- Patient-first EMA/Kareo layout (search + chip relocated below the
    // identity fields; document fields reunited into one section) ----
    const isEmaOrKareo = isEMA || isKareo;
    const isPatientBound = this.state.selectedEmrPatientId !== null && this.state.selectedEmrPatientId !== undefined;
    const handleUnbindPatient = () => this.setState({ selectedEmrPatientId: null });

    // Footer "what's left" summary: names the still-missing required fields when
    // Submit is disabled, so the greyed button isn't a mystery. Derived from the
    // same emaValidationItems descriptor as disableSubmitButton so the two can't
    // drift; other EMRs get a generic prompt.
    const getMissingMessage = () => {
      if (!isEMA) return 'Complete the required fields to continue.';
      const missing = emaValidationItems().filter((item) => item.failed).map((item) => item.message);
      return buildMissingMessage(missing, 'to continue.');
    };

    const renderBoundChartChip = () => {
      if (!isPatientBound) return null;
      return (
        <BoundChartChip
          name={[patientFirstName, patientMiddleName, patientLastName].filter(Boolean).join(' ')}
          dob={patientDOB}
          mrn={isEMA ? this.state.selectedEmrPatientId : ''}
          onChange={handleUnbindPatient}
        />
      );
    };

    const renderPatientMatchSection = () => (
      <>
        <div style={SECTION_HEADING_STYLE}>
          Check for an existing chart
          <span style={SECTION_PILL_STYLE}>From your EMR</span>
        </div>
        {renderBoundChartChip()}
        {/* EMA supports the existing-chart search whether or not the org is on the EMA API:
            non-API EMA orgs search via the EHR-Gateway scraper through the same
            /api/v1/ema_patients endpoint (which forks server-side on the org's
            credentials). Kareo still requires API credentials. */}
        {(apiCredentials || isEMA) && !isPatientBound && (
          <PatientMatchSearch
            getEmaPatients={this.props.getEmaPatients}
            getTebraPatients={this.props.getTebraPatients}
            isKareo={isKareo}
            gatewayEmaSearch={isEMA && !apiCredentials}
            firstName={patientFirstName}
            middleName={patientMiddleName}
            lastName={patientLastName}
            dob={patientDOB}
            onSelectPatient={(record) => this.handlePatientSelection(record)}
          />
        )}
      </>
    );

    const handleDocumentCategoryChange = (categoryName) => {
      const matchedTemplate = this.findTemplateForCategory(categoryName);
      const currentMode = this.state.taskCreationMode;
      let newTaskMode;
      if (this.state.hasUserSelectedTaskMode) {
        newTaskMode = (currentMode === 'auto' && !matchedTemplate) ? 'none' : currentMode;
      } else {
        newTaskMode = matchedTemplate ? 'auto' : 'none';
      }
      this.setState({
        documentCategory: categoryName,
        matchedTaskTemplate: matchedTemplate,
        taskCreationMode: newTaskMode,
      });
    };

    const renderEmaDocumentSection = () => (
      <>
        <div style={SECTION_HEADING_STYLE}>Document</div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Form.Item label={isEMA ? <span>Document Category <span style={{ color: 'red' }}>*</span></span> : 'Document Category'} style={{ flex: 1 }}>
            <Select
              showSearch
              allowClear
              placeholder="Select a document category"
              optionFilterProp="children"
              onChange={handleDocumentCategoryChange}
              value={documentCategory}
            >
              {dedupeDocumentCategories(documentCategories).map((emrCategory) => (
                <Select.Option key={emrCategory.id} value={emrCategory.emr_category}>
                  {emrCategory.emr_category}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Document Title" style={{ flex: 1 }}>
            <Input
              placeholder="e.g. Lab results — June 2026"
              name="fileName"
              value={fileName}
              onChange={handleFieldChange}
            />
          </Form.Item>
        </div>
      </>
    );

    const submitDisabled = disableSubmitButton();
    const whatsLeft = (submitDisabled && isEmaOrKareo) ? getMissingMessage() : null;

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        width="860px"
        action={this.handleSubmit}
        actionName='Submit'
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={submitDisabled}
        isLoading={isLoading}
        modalTitle='Create New Chart'
        form='createNewChart'
        wrapClassName='chart-modal-wrap'
        footerNote={whatsLeft}
        isDraggable
      >
        {
          isInitialLoadComplete ? (
            // Fragment retained (single child) to avoid re-indenting the whole form subtree.
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              <Form
                ref={this.formRef}
                id='createNewChart'
                onFinish={hideModal}
                layout='vertical'
                initialValues={{
                  patientDOB: patientDOB && moment(patientDOB, dateFormat),
                  fileName,
                  populateInsurance,
                  taskAfterUpload: createTaskAfterUpload,
                  form_patient_first_name: patientFirstName,
                  form_patient_middle_name: patientMiddleName,
                  form_patient_last_name: patientLastName,
                }}
              >
              <div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Form.Item
                    label="Patient First Name"
                    name="form_patient_first_name"
                    style={{ flex: 1 }}
                    rules={[
                      {
                        required: true,
                        message: 'Must provide a first name',
                      },
                    ]}
                  >
                    <Input
                      placeholder="First name"
                      name="patientFirstName"
                      value={patientFirstName}
                      onChange={handleFieldChange}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Patient Middle Name"
                    name="form_patient_middle_name"
                    style={{ flex: 1 }}
                  >
                    <Input
                      placeholder="Middle name"
                      name="patientMiddleName"
                      value={patientMiddleName}
                      onChange={handleFieldChange}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Patient Last Name"
                    name="form_patient_last_name"
                    style={{ flex: 1 }}
                    rules={[
                      {
                        required: true,
                        message: 'Must provide a last name',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Last name"
                      name="patientLastName"
                      value={patientLastName}
                      onChange={handleFieldChange}
                    />
                  </Form.Item>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {autoUploadEmr !== 'DrChrono' && (
                  <Form.Item
                    label={isNymbl ? 'Phone Number (optional)' : 'Phone Number'}
                    name='patientPhoneNumber'
                    rules={[
                      {
                        // Show the required asterisk to match disableSubmitButton: EMA needs a
                        // phone for NEW charts (unbound), and the Kareo scraper (no API) and
                        // AdvancedMD always require it. Nymbl keeps it optional.
                        required: (isEMA && !isPatientBound) || (isKareo && !apiCredentials) || autoUploadEmr === 'AdvancedMD',
                        message: 'Must provide a phone number',
                      },
                      {
                        validator: validatePhoneNumber,
                      },
                    ]}
                    onChange={handleFieldChange}
                    style={{ flex: 1 }}
                  >
                    <Input
                      placeholder='Enter phone number'
                      name='patientPhoneNumber'
                      defaultValue={patientPhoneNumber}
                      onChange={handleFieldChange}
                    />
                  </Form.Item>
                )}
                {(isEMA || isNymblWithTenant) && (
                  <Form.Item
                    label="Patient Date of Birth"
                    name="patientDOB"
                    style={{ flex: 1 }}
                    rules={[
                      {
                        required: true,
                        message: 'Must provide a date of birth',
                      },
                    ]}
                  >
                    <DatePicker
                      onChange={handleDateChange}
                      name="patientDOB"
                      format={dateFormat}
                      defaultValue={patientDOB && moment(patientDOB, dateFormat)}
                      value={patientDOB && moment(patientDOB, dateFormat)}
                    />
                  </Form.Item>
                )}
                {/* Gender rides next to DOB as an identity anchor instead of
                    occupying its own full-width row (EMA only). */}
                {isEMA && !isPatientBound && (
                  <Form.Item
                    label="Gender"
                    name="form_gender"
                    style={{ flex: 1 }}
                    rules={[
                      {
                        required: true,
                        message: 'Please select a gender',
                      },
                    ]}
                  >
                    <Radio.Group
                      options={genderOptions}
                      name="patientGender"
                      onChange={handleFieldChange}
                      defaultValue={patientGender}
                    />
                  </Form.Item>
                )}
              </div>
              {!isEMA && !isNymblWithTenant && (
                <Form.Item
                  label="Patient Date of Birth"
                  name="patientDOB"
                  rules={[
                    {
                      required: true,
                      message: 'Must provide a date of birth',
                    },
                  ]}
                >
                  <DatePicker
                    onChange={handleDateChange}
                    format={dateFormat}
                    defaultValue={patientDOB && moment(patientDOB, dateFormat)}
                    value={patientDOB && moment(patientDOB, dateFormat)}
                  />
                </Form.Item>
              )}
              <div style={{ flex: 0.5 }}>
                {(isEMA || isNymblWithTenant || autoUploadEmr === 'Brightree') && (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {isEMA && (
                      <Form.Item label='Secondary Phone Number' style={{ flex: 1 }}>
                        <Input
                          placeholder='Secondary Phone Number'
                          name='secondaryPhone'
                          defaultValue={secondaryPhone}
                          onChange={handleFieldChange}
                        />
                      </Form.Item>
                    )}
                    {(isNymblWithTenant || isEMA || autoUploadEmr === 'Brightree') && <Form.Item label='Email Address' style={{ flex: 1 }}>
                      <Input
                        placeholder='Email Address'
                        name='patientEmail'
                        defaultValue={patientEmail}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>}
                  </div>
                )}
              </div>
              {!isEMA && (
                <div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item
                      label={isNymbl ? 'Gender (optional)' : 'Gender'}
                      style={{ flex: 0.5 }}
                      name='form_gender'
                      rules={[
                        {
                          required: true,
                          message: 'Please select your gender!',
                        },
                      ]}
                    >
                      <Radio.Group
                        options={genderOptions}
                        name='patientGender'
                        onChange={handleFieldChange}
                        defaultValue={patientGender}
                      />
                    </Form.Item>
                  </div>
                </div>
              )}
              {isEmaOrKareo && renderPatientMatchSection()}
              {isEmaOrKareo && renderEmaDocumentSection()}
              {isAdvancedMD && (
                <>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item
                      label={<span><span style={{ color: 'red', fontSize: '10px' }}>*</span> Email Address</span>}
                      style={{ flex: 0.5 }}
                    >
                      <Input
                        placeholder='Email Address'
                        name='patientEmail'
                        onChange={handleFieldChange}
                        defaultValue={patientEmail}
                      />
                    </Form.Item>
                    <Form.Item label={<span><span style={{ color: 'red', fontSize: '10px' }}>*</span> Patient Address</span>} style={{ flex: 0.5 }}>
                      <Input
                        placeholder='Enter address'
                        name='patientAddress'
                        onChange={handleFieldChange}
                        defaultValue={patientAddress}
                      />
                    </Form.Item>
                    <Form.Item
                      name='patientZipCode'
                      label={<span><span style={{ color: 'red', fontSize: '10px'  }}>*</span> Zip Code</span>}
                      style={{ flex: 0.5 }}
                      rules={[
                        {
                          validator: validateZipCode,
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter zip code"
                        name="patientZipCode"
                        onChange={handleFieldChange}
                        defaultValue={patientZipCode}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item label={<span><span style={{ color: 'red', fontSize: '10px'  }}>*</span> Patient City </span>} style={{ flex: 0.5 }}>
                      <Input
                        placeholder="Enter city"
                        name="patientCity"
                        onChange={handleFieldChange}
                        defaultValue={patientCity}
                      />
                    </Form.Item>

                    <Form.Item label={<span><span style={{ color: 'red', fontSize: '10px'  }}>*</span> Provider Name</span>} style={{ flex: 0.5 }}>
                      <Input
                        placeholder="Last name, First name"
                        name="practitionerName"
                        onChange={handleFieldChange}
                        defaultValue={practitionerName}
                      />
                    </Form.Item>
                  </div>
                </>
              )}

              {autoUploadEmr === 'Brightree' && (
                <>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item
                      label="ICD-10 Codes"
                      style={{ flex: 2 }}
                    >
                      <Select
                        mode="tags"
                        placeholder="Enter ICD-10 codes (separated by commas)"
                        style={{ width: '100%' }}
                        onChange={this.handleICD10CodesChange}
                        tokenSeparators={[',']}
                        notFoundContent={null}
                        value={this.state.icd10Codes}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Marketing Representative"
                      name="form_marketing_rep"
                      style={{ flex: 1 }}
                    >
                      <Select
                        placeholder="Select a marketing representative"
                        style={{ width: '100%' }}
                        onChange={this.handleMarketingRepChange}
                        value={this.props.allMarketingRepresentatives?.includes(this.state.marketingRep) ? this.state.marketingRep : null}
                        defaultValue={this.props.allMarketingRepresentatives?.includes(this.state.marketingRep) ? this.state.marketingRep : null}
                        allowClear
                        notFoundContent={
                          (!this.props.allMarketingRepresentatives || this.props.allMarketingRepresentatives.length === 0)
                            ? 'No marketing representatives found. Please contact your admin to add them.'
                            : null
                        }
                      >
                        {this.props.allMarketingRepresentatives && this.props.allMarketingRepresentatives.map((rep, index) => (
                          <Select.Option key={`${rep}-${index}`} value={rep}>
                            {rep}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item
                      label="Primary Payer Name"
                      name="form_primary_payer_name"
                      style={{ flex: 1 }}
                    >
                      <Input
                        placeholder="Primary payer name"
                        name="form_primary_payer_name"
                        defaultValue={primaryPlan}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Primary Policy Number"
                      name="form_primary_policy_number"
                      style={{ flex: 1 }}
                    >
                      <Input
                        placeholder="Primary policy number"
                        name="form_primary_policy_number"
                        defaultValue={primaryMemberId}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item
                      label="Primary Payer PO Box"
                      name="form_primary_payer_address"
                      style={{ flex: 1 }}

                    >
                      <Input
                        placeholder="Primary payer PO box"
                        name="form_primary_payer_address"
                        defaultValue={this.state.primaryPayerAddress}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Primary Payer State"
                      name="form_primary_payer_state"
                      style={{ flex: 1 }}
                    >
                      <Select
                        showSearch
                        placeholder="Primary Payer State"
                        defaultValue={this.state.primaryPayerState}
                        onChange={(value) => this.setState({ primaryPayerState: value })}
                        filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        allowClear
                      >
                        {usStates.map((state) => (
                          <Select.Option key={state.value} value={state.value}>
                            {state.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      label="Primary Group Number (Optional)"
                      style={{ flex: 1 }}
                    >
                      <Input
                        placeholder="Primary Group Number"
                        name="primaryGroupNumber"
                        defaultValue={primaryGroupNumber}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item
                      label="Secondary Payer Name"
                      name="form_secondary_payer_name"
                      style={{ flex: 1 }}
                    >
                      <Input
                        placeholder="Secondary Payer Name"
                        name="form_secondary_payer_name"
                        defaultValue={secondaryPlan}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Secondary Policy Number"
                      name="form_secondary_policy_number"
                      style={{ flex: 1 }}
                    >
                      <Input
                        placeholder="Secondary policy number"
                        name="form_secondary_policy_number"
                        defaultValue={secondaryMemberId}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item
                      label="Secondary payer PO box"
                      name="form_secondary_payer_po_box"
                      style={{ flex: 1 }}
                    >
                      <Input
                        placeholder="Secondary payer PO box"
                        name="form_secondary_payer_po_box"
                        defaultValue={this.state.secondaryPayerAddress}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Secondary Payer State"
                      name="form_secondary_payer_state"
                      style={{ flex: 1 }}
                    >
                      <Select
                        showSearch
                        placeholder="Secondary payer state"
                        defaultValue={this.state.secondaryPayerState}
                        onChange={(value) => this.setState({ secondaryPayerState: value })}
                        filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        allowClear
                      >
                        {usStates.map((state) => (
                          <Select.Option key={state.value} value={state.value}>
                            {state.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      label="Secondary Group Number (Optional)"
                      style={{ flex: 1 }}
                    >
                      <Input
                        placeholder="Secondary group number"
                        name="secondaryGroupNumber"
                        defaultValue={secondaryGroupNumber}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                  </div>
                </>
              )}
              {(isEMA || isNymblWithTenant || isKareo) && (
                <div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item label='Patient Address' style={{ flex: 1 }}>
                      <Input
                        placeholder='Patient Address'
                        name='patientAddress'
                        defaultValue={patientAddress}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item label='City' style={{ flex: 0.4 }}>
                      <Input
                        placeholder='City'
                        name='patientCity'
                        defaultValue={patientCity}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item label='State' style={{ flex: 0.2 }}>
                      <Input
                        placeholder=''
                        name='patientState'
                        defaultValue={patientState}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item label='Zip' style={{ flex: 0.3 }}>
                      <Input
                        placeholder=''
                        name='patientZipCode'
                        defaultValue={patientZipCode}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                  </div>
                  {isEMA && (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item
                      label="Referring Practice"
                      style={{ flex: 1 }}
                    >
                      <Input
                        placeholder="Referring Practice"
                        name="providerOffice"
                        defaultValue={providerOffice}
                        onChange={handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      label={
                        (
                          <span>
                            Referring Provider
                            {isEMA && apiCredentials && (
                              <Tooltip title="Start typing to search and select a matching provider from your EMR. If our AI extracted the provider for you, you still need to select the provider from the dropdown to match it to a referring provider from your EMR. You can also leave this field blank.">
                                <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 16, marginLeft: 4 }} />
                              </Tooltip>
                            )}
                          </span>
                        )
                      }
                      style={{ flex: 1 }}
                      validateStatus={
                        isEMA && apiCredentials && referringProvider && !referringProviderId
                          ? 'error'
                          : undefined
                      }
                      help={
                        isEMA && apiCredentials && referringProvider && !referringProviderId
                          ? 'Please select a provider from the dropdown or leave blank.'
                          : undefined
                      }
                    >
                      {isEMA && apiCredentials ? (
                        <ReferringProviderAutocomplete
                          placeholder="Type to search providers"
                          name="referringProvider"
                          value={referringProvider}
                          onChange={handleReferringProviderChange}
                        />
                      ) : (
                        <Input
                          placeholder='Referring Provider'
                          name='referringProvider'
                          defaultValue={referringProvider}
                          onChange={handleFieldChange}
                        />
                      )}
                    </Form.Item>
                    </div>
                  )}
                  {
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {/* Keep checkbox for Kareo */}
                      {canCreateTaskAfterUpload && isKareo && (
                        <Form.Item
                          style={{ display: 'inline-block' }}
                          name="taskAfterUpload"
                          valuePropName="checked"
                          onChange={this.handleCreateTaskAfterUpload}
                        >
                          <Checkbox>{taskCheckboxLabel}</Checkbox>
                        </Form.Item>
                      )}
                      {allPayerNames && allPayerNames.length > 0 &&
                        <Form.Item
                          name="populateInsurance"
                          valuePropName="checked"
                          onChange={handlePopulateInsurance}
                        >
                        <Checkbox>Populate insurance information</Checkbox>
                        </Form.Item>
                    }
                    </div>
                  }
                  {populateInsurance && allPayerNames && allPayerNames.length > 0 &&
                  <>
                    {isKareo && (
                      <Form.Item
                        label="Case Name (Optional)"
                        style={{ marginBottom: '16px' }}
                      >
                        <Input
                          placeholder="Case Name"
                          name="caseName"
                          value={this.state.caseName}
                          onChange={handleFieldChange}
                        />
                      </Form.Item>
                    )}
                    <span style={sectionHeadingStyle}>Primary Insurance</span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Form.Item
                        label={<span>Payer<span style={{ color: 'red', fontSize: '12px' }}> * {primaryPlan && !allPayerNames.includes(primaryPlan) && 'Select payer from the dropdown'}</span></span>}
                        style={{ flex: 0.7 }}
                        rules={[
                          {
                            required: true,
                            message: 'Please select the payer name!',
                          },
                        ]}
                      >
                        {allPayerNames.length > 0 ? (
                          <Select
                            showSearch
                            placeholder="Payer"
                            value={primaryPlan}
                            optionFilterProp="children"
                            allowClear
                            onClear={() => {
                              this.setState({
                                primaryPlan: '',
                                primaryPlanName: '',
                                primaryMemberId: '',
                                primaryGroupNumber: '',
                                primaryPolicyType: '',
                                primaryPolicyTypeLocked: false,
                                // Reset authorization when primary insurance is cleared
                                addAuthorization: false,
                                authorizationType: '',
                                authorizationNumber: '',
                                authorizationDescription: '',
                                authorizationActiveStatus: true,
                                authorizationUnlimitedVisits: false,
                                authorizationNumberOfVisits: '',
                                authorizationFromDate: '',
                                authorizationToDate: '',
                                authorizationNote: '',
                                authorizationReferringProvider: '',
                              });
                              // Sync Form state with component state
                              if (this.formRef.current) {
                                this.formRef.current.setFieldsValue({
                                  primaryMemberId: '',
                                  primaryGroupNumber: '',
                                });
                              }
                            }}
                            onChange={handlePayerNameChange('primary')}
                            onSearch={handlePrimaryPayerNameSearch}
                            filterOption={false}
                            style={{ width: '100%' }}
                          >
                            {searchedPrimaryPayerNames.map(e => (
                              <Select.Option key={e} value={e}>
                                {e}
                              </Select.Option>
                            ))}
                          </Select>
                        ) : (
                          <Input
                            disabled
                            placeholder={noPayerNameMessage}
                          />
                        )}
                      </Form.Item>
                      <Form.Item
                        label={<span>Policy Number <span style={{ color: 'red' }}>*</span></span>}
                        style={{ flex: 0.3 }}
                        name="primaryMemberId"
                        initialValue={primaryMemberId}
                        rules={[
                          {
                            validator: validateAlphanumeric,
                          },
                        ]}
                      >
                        <Input
                          placeholder='Policy Number'
                          name='primaryMemberId'
                          onChange={handleFieldChange}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </div>
                    {shouldShowPlanNameField && renderPlanNameSelect('primary', primaryPlan, primaryPlanName)}
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Form.Item
                        name="primaryGroupNumber"
                        label="Group Number (Optional)"
                        style={{ flex: 0.5 }}
                        initialValue={primaryGroupNumber}
                        rules={[
                          {
                            validator: validateAlphanumeric,
                          },
                        ]}
                      >
                        <Input
                          placeholder="Group Number"
                          name="primaryGroupNumber"
                          onChange={handleFieldChange}
                        />
                      </Form.Item>
                      {!isKareo && (
                        // hide policy type for Kareo since it's not supported
                        <Form.Item
                          label={<span>Policy Type <span style={{ color: 'red' }}>*</span></span>}
                          style={{ flex: 0.5 }}
                        >
                          <Select
                            placeholder="Policy Type"
                            value={primaryPolicyType}
                            onSelect={handlePolicyTypeChange('primary')}
                            showSearch
                            optionFilterProp="children"
                            disabled={this.state.primaryPolicyTypeLocked}
                          >
                          {this.state.policyTypeOptions.map(e => (
                                <Select.Option key={e} value={e}>
                                  {e}
                                </Select.Option>
                              ))}
                          </Select>
                        </Form.Item>
                      )}
                    </div>
                    {!(this.state.showSecondaryInsurance || secondaryInsurance) ? (
                      <button
                        type="button"
                        className="chart-modal__link-btn"
                        onClick={() => this.setState({ showSecondaryInsurance: true })}
                      >
                        + Add secondary insurance
                      </button>
                    ) : (
                    <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={sectionHeadingStyle}>Secondary Insurance</span>
                      <button
                        type="button"
                        className="chart-modal__link-btn chart-modal__link-btn--muted"
                        onClick={() => this.setState({
                          showSecondaryInsurance: false,
                          secondaryInsurance: false,
                          secondaryPlan: '',
                          secondaryPlanName: '',
                          secondaryMemberId: '',
                          secondaryGroupNumber: '',
                          secondaryPolicyType: '',
                        })}
                      >
                        Remove
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Form.Item
                        label={<span>Payer <span style={{ color: 'red', fontSize: '12px' }}>{secondaryPlan && !allPayerNames.includes(secondaryPlan) && 'Select payer from the dropdown'}</span></span>}
                        style={{ flex: 0.7 }}
                      >
                        {allPayerNames.length > 0 ? (
                          <Select
                            showSearch
                            placeholder="Payer"
                            value={secondaryPlan}
                            allowClear
                            onClear={() => this.setState({
                              secondaryPlan: '',
                              secondaryPlanName: '',
                              secondaryMemberId: '',
                              secondaryGroupNumber: '',
                              secondaryPolicyType: '',
                              secondaryInsurance: false,
                            })}
                            optionFilterProp="children"
                            onChange={handlePayerNameChange('secondary')}
                            onSearch={handleSecondaryPayerNameSearch}
                            filterOption={false}
                            style={{ width: '100%' }}
                          >
                            {searchedSecondaryPayerNames.map(e => (
                              <Select.Option key={e} value={e}>
                                {e}
                              </Select.Option>
                            ))}
                          </Select>
                        ) : (
                          <Input
                            disabled
                            placeholder={noPayerNameMessage}
                          />
                        )}
                      </Form.Item>
                      <Form.Item
                        name="secondaryMemberId"
                        initialValue={secondaryMemberId}
                        label={<span>Policy Number</span>}
                        style={{ flex: 0.3 }}
                        rules={[
                          {
                            validator: validateAlphanumeric,
                          },
                        ]}
                      >
                        <Input
                          placeholder='Policy Number'
                          name='secondaryMemberId'
                          onChange={handleFieldChange}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </div>
                    {shouldShowPlanNameField && renderPlanNameSelect('secondary', secondaryPlan, secondaryPlanName)}
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Form.Item
                        name="secondaryGroupNumber"
                        initialValue={secondaryGroupNumber}
                        label="Group Number (Optional)"
                        style={{ flex: 0.5 }}
                        rules={[
                          {
                            validator: validateAlphanumeric,
                          },
                        ]}
                      >
                        <Input
                          placeholder="Group Number"
                          name="secondaryGroupNumber"
                          onChange={handleFieldChange}
                        />
                      </Form.Item>
                      {autoUploadEmr !== 'Kareo' && (
                        // Hide policy type for Kareo since it's not supported
                        <Form.Item
                          label={<span>Policy Type</span>}
                          style={{ flex: 0.5 }}
                        >
                          <Select
                            placeholder="Policy Type"
                            value={secondaryPolicyType}
                            onSelect={handlePolicyTypeChange('secondary')}
                            showSearch
                            optionFilterProp="children"
                            disabled={this.state.secondaryPolicyTypeLocked}
                          >
                          {this.state.policyTypeOptions.map(e => (
                                <Select.Option key={e} value={e}>
                                  {e}
                                </Select.Option>
                              ))}
                          </Select>
                        </Form.Item>
                      )}
                    </div>
                    </>
                    )}
                  </>
                  }
                  {/* Authorization Section - Only show for EMA when primary insurance is filled and feature flag enabled */}
                  {isEMA && populateInsurance && primaryPlan && primaryMemberId && this.state.canExtractAuthorization && (
                    <>
                      <Divider />
                      <Form.Item>
                        <Checkbox
                          checked={this.state.addAuthorization}
                          onChange={(e) => {
                            this.setState({ addAuthorization: e.target.checked });
                            if (!e.target.checked) {
                              // Reset authorization fields when unchecked
                              this.setState({
                                authorizationType: '',
                                authorizationNumber: '',
                                authorizationDescription: '',
                                authorizationActiveStatus: true,
                                authorizationUnlimitedVisits: false,
                                authorizationNumberOfVisits: '',
                                authorizationFromDate: '',
                                authorizationToDate: '',
                                authorizationNote: '',
                                authorizationReferringProvider: '',
                              });
                            }
                          }}
                        >
                          Add Insurance Authorization
                        </Checkbox>
                      </Form.Item>
                      {this.state.addAuthorization && (
                        <>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <Form.Item
                              label={<span>Authorization Type <span style={{ color: 'red' }}>*</span></span>}
                              style={{ flex: 0.5 }}
                            >
                              <Select
                                placeholder="Select type"
                                value={this.state.authorizationType || undefined}
                                onChange={(value) => this.setState({ authorizationType: value })}
                              >
                                {authorizationTypeOptions.map((option) => (
                                  <Select.Option key={option} value={option}>
                                    {option}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              label={<span>Authorization Number <span style={{ color: 'red' }}>*</span></span>}
                              style={{ flex: 0.5 }}
                            >
                              <Input
                                placeholder="Authorization number"
                                value={this.state.authorizationNumber}
                                onChange={(e) => this.setState({ authorizationNumber: e.target.value })}
                              />
                            </Form.Item>
                          </div>
                          <Form.Item
                            label={<span>Description <span style={{ color: 'red' }}>*</span></span>}
                          >
                            <Input
                              placeholder="Authorization description"
                              value={this.state.authorizationDescription}
                              onChange={(e) => this.setState({ authorizationDescription: e.target.value })}
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                            {!this.state.authorizationUnlimitedVisits && (
                              <Form.Item
                                label={<span>Number of Visits <span style={{ color: 'red' }}>*</span></span>}
                                style={{ flex: 0.3 }}
                              >
                                <Input
                                  type="number"
                                  placeholder="Visits"
                                  value={this.state.authorizationNumberOfVisits}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || (parseInt(value, 10) >= 1 && parseInt(value, 10) <= 99)) {
                                      this.setState({ authorizationNumberOfVisits: value });
                                    }
                                  }}
                                  min={1}
                                  max={99}
                                />
                              </Form.Item>
                            )}
                            <Form.Item
                              label="Status"
                              style={{ flex: 0.35 }}
                            >
                              <Select
                                value={this.state.authorizationActiveStatus ? 'Active' : 'Inactive'}
                                onChange={(value) => this.setState({ authorizationActiveStatus: value === 'Active' })}
                              >
                                <Select.Option value="Active">Active</Select.Option>
                                <Select.Option value="Inactive">Inactive</Select.Option>
                              </Select>
                            </Form.Item>
                            <Form.Item style={{ flex: 0.35, marginBottom: '24px' }}>
                              <Checkbox
                                checked={this.state.authorizationUnlimitedVisits}
                                onChange={(e) => {
                                  this.setState({
                                    authorizationUnlimitedVisits: e.target.checked,
                                    // Clear number of visits when unlimited is checked
                                    authorizationNumberOfVisits: e.target.checked ? '' : this.state.authorizationNumberOfVisits,
                                  });
                                }}
                              >
                                Unlimited Visits
                              </Checkbox>
                            </Form.Item>
                          </div>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <Form.Item
                              label={<span>From Date {this.state.authorizationUnlimitedVisits && <span style={{ color: 'red' }}>*</span>}</span>}
                              style={{ flex: 0.5 }}
                            >
                              <DatePicker
                                format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                                value={this.state.authorizationFromDate ? moment(this.state.authorizationFromDate, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']) : null}
                                onChange={(_, dateString) => this.setState({ authorizationFromDate: dateString })}
                                placeholder="From date"
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                            <Form.Item
                              label={<span>To Date {this.state.authorizationUnlimitedVisits && <span style={{ color: 'red' }}>*</span>}</span>}
                              style={{ flex: 0.5 }}
                            >
                              <DatePicker
                                format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                                value={this.state.authorizationToDate ? moment(this.state.authorizationToDate, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']) : null}
                                onChange={(_, dateString) => this.setState({ authorizationToDate: dateString })}
                                placeholder="To date"
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </div>
                          <Form.Item
                            label={
                              (
                                <span>
                                  Referring Provider (optional)
                                  {apiCredentials && (
                                    <Tooltip title="Start typing to search and select a matching provider from your EMR.">
                                      <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 16, marginLeft: 4 }} />
                                    </Tooltip>
                                  )}
                                </span>
                              )
                            }
                          >
                            {apiCredentials ? (
                              <ReferringProviderAutocomplete
                                placeholder="Type to search providers"
                                name="authorizationReferringProvider"
                                value={this.state.authorizationReferringProvider}
                                onChange={(providerId, providerName) => this.setState({ authorizationReferringProvider: providerName })}
                              />
                            ) : (
                              <Input
                                placeholder="Referring provider"
                                value={this.state.authorizationReferringProvider}
                                onChange={(e) => this.setState({ authorizationReferringProvider: e.target.value })}
                              />
                            )}
                          </Form.Item>
                          <Form.Item
                            label="Note (optional)"
                          >
                            <Input
                              placeholder="Authorization note"
                              value={this.state.authorizationNote}
                              onChange={(e) => this.setState({ authorizationNote: e.target.value })}
                            />
                          </Form.Item>
                        </>
                      )}
                    </>
                  )}
                  {/* Radio buttons for EMA - after insurance section */}
                  {canCreateTaskAfterUpload && isEMA && (
                    <Form.Item label={<span style={{ fontSize: '18px' }}>Task Creation</span>} style={{ marginBottom: '8px' }}>
                      <Radio.Group
                        value={taskCreationMode}
                        onChange={(e) => this.setState({
                          taskCreationMode: e.target.value,
                          hasUserSelectedTaskMode: true,
                        })}
                      >
                        {matchedTaskTemplate && (
                          <Radio value="auto" style={{ display: 'block', marginBottom: '8px' }}>
                            Auto-task with "{matchedTaskTemplate.task_type}"
                            <span style={{ color: '#888', marginLeft: '8px', fontSize: '12px' }}>
                              ({documentCategory} template · {matchedTaskTemplate.scope === 'Organization' ? 'Org' : matchedTaskTemplate.scope}: {matchedTaskTemplate.scope_name})
                            </span>
                            <Tooltip title="A task will be automatically created in your EMR using the pre-configured template settings for this document category. The task type, assignee, and details are already defined in your template - no additional input needed.">
                              <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 14, marginLeft: 6 }} />
                            </Tooltip>
                          </Radio>
                        )}
                        <Radio value="custom" style={{ display: 'block', marginBottom: '8px' }}>
                          Create task with custom details
                          <Tooltip title="You will manually specify the task details below, including the assignee, task type, and any additional notes. Use this option when you need to customize the task for this specific document.">
                            <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 14, marginLeft: 6 }} />
                          </Tooltip>
                        </Radio>
                        <Radio value="none" style={{ display: 'block' }}>
                          Do not create a task
                        </Radio>
                      </Radio.Group>
                    </Form.Item>
                  )}
                  {((isEMA && taskCreationMode === 'custom') || (isKareo && createTaskAfterUpload)) &&
                    <>
                      {populateInsurance && allPayerNames && allPayerNames.length > 0 &&
                      <>
                        <Divider />
                      </>
                      }
                      <div style={{ display: 'flex', gap: '10px' }}>
                        {canUseTaskTemplates
                        && (
                        <Form.Item
                          label="Task Templates"
                          name="taskTemplates"
                          style={{ flex: 0.5 }}
                        >
                          <Select
                            showSearch
                            optionFilterProp="children"
                            onChange={this.handleTaskTemplateChange}
                            onSearch={this.handleTaskTemplateSearch}
                            filterOption={false}
                            allowClear
                          >
                            {this.state.searchedTemplates && this.state.searchedTemplates.map(e => (
                              <Select.Option key={e.id} value={e.id}>
                                {e.task_name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        )}
                    {autoUploadEmr === 'EMA' && apiCredentials
                        && (
                        <Form.Item
                            label="Facility (Optional)"
                            style={{ flex: 0.4 }}
                          >
                            <Select
                              showSearch
                              placeholder="Facility"
                              value={this.state.facilityName}
                              optionFilterProp="children"
                              onChange={this.handleFacilityChange}
                              onSearch={this.handleFacilitySearch}
                              filterOption={false}
                              allowClear
                            >
                              {this.state.searchedFacilities.map(facility => (
                                <Select.Option key={facility.id} value={facility.name}>
                                  {facility.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )}  
                      </div>
                      {isKareo ? (
                        <div style={{ display: 'flex', gap: '10px' }}>
                          {this.renderAssignToSelect()}
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <Form.Item
                              label={isKareo ? "Task (Optional)" : "Task"}
                              style={{ flex: 0.35 }}
                            >
                              <Select
                                showSearch
                                placeholder="Task"
                                value={this.state.task}
                                optionFilterProp="children"
                                onChange={e => this.handleSelectionChange(e, 'task')}
                                onSearch={this.handleTaskSearch}
                                filterOption={false}
                              >
                                {this.state.searchedTasks.map(e => (
                                  <Select.Option key={e} value={e}>
                                    {e}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              label={isKareo ? "Task Details (Optional)" : "Task Details"}
                              style={{ flex: 0.35 }}
                            >
                              <AutoComplete
                                placeholder="Task Details"
                                value={this.state.taskDetails}
                                options={this.state.taskDetailsSearchOptions.map(option => ({ value: option }))}
                                onSearch={this.handleSearchTaskDetails}
                                onSelect={e => this.handleSelectionChange(e, 'taskDetails')}
                              />
                            </Form.Item>
                            <Form.Item
                              label="Due Date"
                              style={{ flex: 0.3 }}
                            >
                              <DatePicker
                                onChange={this.handleDueDateChange}
                                name="dueDate"
                                format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                                value={this.state.dueDate && moment(this.state.dueDate, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
                              />
                            </Form.Item>
                          </div>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            {this.renderAssignToSelect()}
                            <Form.Item
                              label="Priority"
                              style={{ flex: 0.5 }}
                            >
                              <Select
                                allowClear={false}
                                value={this.state.priority}
                                options={priorityOptions.map(option => ({ value: option }))}
                                onChange={e => this.handleSelectionChange(e, 'priority')}
                              />
                            </Form.Item>
                          </div>
                        </>
                      )}
                      <Form.Item
                        name="notes"
                        label="Notes (optional)"
                      >
                        <Input.TextArea
                          onChange={this.handleNotesChange}
                          placeholder="Notes (optional)"
                          name="notes"
                          value={this.state.notes}
                        />
                      </Form.Item>
                    </>             
                  }
                </div>
              )}
              {isAdvancedMD && (
                <>
                  {allPayerNames && allPayerNames.length > 0
                  && (
                    <Form.Item
                      name="populateInsurance"
                      valuePropName="checked"
                      onChange={handlePopulateInsurance}
                    >
                      <Checkbox>Populate insurance information</Checkbox>
                    </Form.Item>
                  )}
                  {populateInsurance && allPayerNames && allPayerNames.length > 0
                  && (
                    <>
                      <span style={sectionHeadingStyle}>Primary Insurance</span>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Form.Item
                          name="primaryPlan"
                          label={(
                            <span>
                              Payer {primaryPlan && !allPayerNames.includes(primaryPlan) && 'Select carrier from the dropdown'}
                            </span>
                          )}
                          style={{ flex: 0.7 }}
                          rules={[
                            {
                              required: true,
                              message: 'Please select the carrier name!',
                            },
                          ]}
                        >
                          {allPayerNames.length > 0 ? (
                            <Select
                              showSearch
                              placeholder="Payer"
                              value={primaryPlan}
                              optionFilterProp="children"
                              allowClear
                              onClear={() => this.setState({
                                primaryPlan: '',
                                primaryMemberId: '',
                                primaryGroupNumber: '',
                                primaryPolicyType: '',
                              })}
                              onChange={handlePayerNameChange('primary')}
                              onSearch={handlePrimaryPayerNameSearch}
                              filterOption={false}
                              style={{ width: '100%' }}
                            >
                              {searchedPrimaryPayerNames.map((e) => (
                                <Select.Option key={e} value={e}>
                                  {e}
                                </Select.Option>
                              ))}
                            </Select>
                          ) : (
                            <Input
                              disabled
                              placeholder={noPayerNameMessage}
                            />
                          )}
                        </Form.Item>
                        <Form.Item
                          label="Subscriber ID"
                          style={{ flex: 0.3 }}
                          name="primaryMemberId"
                          rules={[
                            {
                              required: true,
                              message: 'Please enter the Subscriber ID!',
                            },
                          ]}
                        >
                          <Input
                            placeholder="Subscriber ID"
                            name="primaryMemberId"
                            value={primaryMemberId}
                            onChange={handleFieldChange}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                        <Form.Item
                          label={<span><span style={{ color: 'red', fontSize: '10px'  }}>*</span> Effective Date</span>}
                          name="effectiveDate"
                        >
                          <DatePicker
                            onChange={handleEffectiveDateChange}
                            name="insuranceEffectiveDate"
                            format={dateFormat}
                            value={insuranceEffectiveDate && moment(insuranceEffectiveDate, dateFormat)}
                          />
                        </Form.Item>
                      </div>
                    </>
                  )}
                </>
              )}
              {!['DrChrono', 'Brightree'].includes(autoUploadEmr) && !isEmaOrKareo && (autoUploadEmr !== 'EMA' || apiCredentials) && (
                <Form.Item label="Document Title (optional)">
                  <Input
                    placeholder='Document title'
                    name='fileName'
                    onChange={handleFieldChange}
                    value={fileName}
                  />
                </Form.Item>
              )}
              {(localStorage.m8_org === 'PrePost+' && practiceName)
                && (
                  <Form
                    initialValues={{
                      locationSelection: practiceName,
                    }}
                  >
                  <Form.Item
                    style={{ display: 'flex', marginTop: 20 }}
                    name="locationSelection"
                    label="Location"
                  >
                    <Select
                      onChange={value => this.setState({ practiceName: value })}
                      value={practiceName}
                      options={[
                        { value: 'PrePost+ Clarksville', label: 'PrePost+ Clarksville' },
                        { value: 'PrePost+ Nashville (Midtown)', label: 'PrePost+ Nashville (Midtown)' },
                        { value: 'PrePost+ Tennessee', label: 'PrePost+ Tennessee' },
                      ]}
                    >
                    </Select>
                  </Form.Item>
                  </Form>
                )
              }
            </Form>
            </>
          ) : (
          <Spin
            style={{ marginTop: '5px', marginLeft: '50%' }} // Adjust positioning as needed
          />
          )
        }
      </ModalWrapper>
    );
  }
}

CreateNewChart.defaultProps = {
  error: false,
  errorMessage: null,
  receivedAt: '',
  callerName: '',
  allMarketingRepresentatives: [],
  apiError: false,
  apiErrorMessage: '',
};

CreateNewChart.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  triggerAutoUpload: PropTypes.func.isRequired,
  refreshCurrentRecords: PropTypes.func.isRequired,
  triggerAutoUploadAndTaskCreation: PropTypes.func.isRequired,
  selectedRecord: PropTypes.array.isRequired,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  receivedAt: PropTypes.string,
  callerName: PropTypes.string,
  allDocumentCategories: PropTypes.object.isRequired,
  allPayerNames: PropTypes.array.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  getPayerNames: PropTypes.func.isRequired,
  getEmaPatients: PropTypes.func.isRequired,
  getTebraPatients: PropTypes.func.isRequired,
  apiPatients: PropTypes.object.isRequired,
  apiError: PropTypes.bool,
  getEmaTaskProviders: PropTypes.func.isRequired,
  getKareoTaskProviders: PropTypes.func.isRequired,
  getMarketingRepresentatives: PropTypes.func.isRequired,
  getTaskTemplatesByQuery: PropTypes.func.isRequired,
  allEmaTaskProviders: PropTypes.array.isRequired,
  allKareoTaskProviders: PropTypes.array,
  allEmaTaskTemplates: PropTypes.object.isRequired,
  allMarketingRepresentatives: PropTypes.array,
  apiErrorMessage: PropTypes.string,
};

export default connect((state) => ({
  isLoading: state.records.isLoading,
  emailValidMessage: state.records.emailValidMessage,
  updatedLabel: state.records.updatedLabel,
  data: state.labels.data,
  selectedRecord: state.inboxTable.recordData,
  error: state.records.error,
  errorMessage: state.records.errorMessage,
  receivedAt: state.records.receivedAt,
  callerName: state.records.callerName,
  allDocumentCategories: state.userProfile.allDocumentCategories,
  allPayerNames: state.userProfile.allPayerNames,
  payerPlanNamesMap: state.userProfile.payerPlanNamesMap,
  apiError: state.userProfile.apiError,
  apiPatients: state.userProfile.apiPatients,
  apiErrorMessage: state.userProfile.apiErrorMessage,
  allEmaTaskTemplates: state.userProfile.allEmaTaskTemplates,
  allEmaTaskProviders: state.userProfile.allEmaTaskProviders,
  allKareoTaskProviders: state.userProfile.allKareoTaskProviders,
  allMarketingRepresentatives: state.userProfile.allMarketingRepresentatives,
}))(CreateNewChart);
